import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FileSpreadsheet, Upload, Download, Trash2, RefreshCw, Search, X, ChevronDown,
  AlertTriangle, TrendingUp, Wallet, Loader2, Eye, EyeOff, Columns3,
  Link2, Copy, Ban, RotateCcw, FileText, Mail, Send, Bell, Plus, Pencil,
  ShieldAlert, Undo2, ShieldOff, UserRoundPen, ArrowUp, ArrowDown, Minus, Layers,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import CustomerMultiSelect from '../components/CustomerMultiSelect';
import SalesmanRecipientsModal from '../components/SalesmanRecipientsModal';
import ManualRecipientsModal from '../components/ManualRecipientsModal';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_COL_KEY = 'admin_outstanding_cols_v1';

// Aging bucket keys — must match backend `AGING_KEYS`. Used by
// aging-only column mode (Feb 2026 user request): when the admin
// selects a single aging bucket from the dropdown, every OTHER aging
// column collapses out of the table so the view focuses on the one
// bucket that matters, and the KPI tiles reduce to that bucket's
// total.
const AGING_KEYS = ['curr', 'b30', 'b61', 'b91', 'b122', 'b150', 'b181', 'over181'];
const AGING_LABEL = {
  curr: 'Current',
  b30: '≤30 days',
  b61: '≤61 days',
  b91: '≤91 days',
  b122: '≤122 days',
  b150: '≤150 days',
  b181: '≤181 days',
  over181: '>181 days',
};

// Column definitions — keep in sync with backend `OS_COLUMNS`. `alwaysOn`
// columns (code + name) cannot be hidden. `heat` marks the aging cells
// that render with the traffic-light heatmap.
const COLS = [
  { key: 'customer_code', label: 'Cust',        w: 90,   align: 'left',  alwaysOn: true },
  { key: 'name',          label: 'Customer',    w: 240,  align: 'left',  alwaysOn: true },
  { key: 'category',      label: 'Division',    w: 110,  align: 'left' },
  { key: 'dept',          label: 'Dept',        w: 80,   align: 'left' },
  { key: 'cust_type',     label: 'Type',        w: 96,   align: 'left' },
  { key: 'division',      label: 'ERP Div',     w: 160,  align: 'left' },
  { key: 'salesman',      label: 'Salesman',    w: 140,  align: 'left' },
  { key: 'credit_days',   label: 'Cr Days',     w: 80,   align: 'right' },
  { key: 'credit_limit',  label: 'Cr Limit',    w: 110,  align: 'right' },
  { key: 'credit_type',   label: 'Cr Type',     w: 90,   align: 'left' },
  { key: 'dn_amt',        label: 'DN Amt',      w: 110,  align: 'right' },
  { key: 'os_amt',        label: 'OS Amt',      w: 110,  align: 'right' },
  { key: 'pdc_amt',       label: 'PDC Amt',     w: 100,  align: 'right' },
  { key: 'uncovered',     label: 'Uncovred',    w: 110,  align: 'right' },
  { key: 'curr',          label: 'Current',     w: 100,  align: 'right', heat: 'curr' },
  { key: 'b30',           label: '≤30',         w: 88,   align: 'right', heat: 'b30' },
  { key: 'b61',           label: '≤61',         w: 88,   align: 'right', heat: 'b61' },
  { key: 'b91',           label: '≤91',         w: 88,   align: 'right', heat: 'b91' },
  { key: 'b122',          label: '≤122',        w: 88,   align: 'right', heat: 'b122' },
  { key: 'b150',          label: '≤150',        w: 88,   align: 'right', heat: 'b150' },
  { key: 'b181',          label: '≤181',        w: 88,   align: 'right', heat: 'b181' },
  { key: 'over181',       label: '>181',        w: 96,   align: 'right', heat: 'over181' },
  { key: 'total_os',      label: 'Total OS',    w: 120,  align: 'right', bold: true },
];

// Heatmap palette. Empty (zero / null) cells stay neutral so admins can
// scan for the actual pain points at a glance.
const HEAT_BG = {
  curr:    'bg-emerald-50 text-emerald-700',
  b30:     'bg-lime-50    text-lime-700',
  b61:     'bg-yellow-50  text-yellow-800',
  b91:     'bg-amber-100  text-amber-800',
  b122:    'bg-orange-100 text-orange-800',
  b150:    'bg-red-100    text-red-700',
  b181:    'bg-red-200    text-red-800',
  over181: 'bg-red-300    text-red-900 font-semibold',
};

// Numeric columns that are per-customer SCALARS (not summable) and
// should render as WHOLE NUMBERS (no baisa). Applies to the on-screen
// table cell + footer TOTAL row on both admin dashboard and public
// share (matches XLSX + PDF exports). Feb 2026 user request — summing
// Cr Days / Cr Limit produces meaningless numbers.
const NON_SUMMABLE_KEYS = new Set(['credit_days', 'credit_limit']);
const INT_FORMAT_KEYS   = new Set(['credit_days', 'credit_limit']);

const fmtInt = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '\u2014';
  return Math.round(v).toLocaleString('en-US');
};

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '—';
  // Omani Rial — 3 decimals (baisa). Applied uniformly on every KPI
  // tile, table cell, per-category breakdown, and footer total across
  // the Outstanding module (matches product/order/cart formatters).
  return v.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

// Multi-select checkbox popover used by the share-link creator to bake
// multiple salesmen / divisions onto one link (Feb 2026 user request).
// Keeps the pill-shaped trigger compatible with the two single-value
// `<select>`s it replaced — same height, same border, same font. The
// controlled `value` is an array of strings; passing `[]` means "no
// lock" and the mint payload omits that facet entirely so the link
// stays unbaked. Existing single-value links stored in the DB as
// scalars continue to work — the backend accepts both shapes.
const MultiSelectFacet = ({ testId, placeholder, options, value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  // Type-ahead filter (Feb 2026 UX request) — critical for the salesman
  // list which grows to 40-60 entries at production scale. The query
  // is reset whenever the popover closes so a fresh open always shows
  // the full list.
  const [query, setQuery] = React.useState('');
  const containerRef = React.useRef(null);
  const searchRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  React.useEffect(() => {
    if (open) {
      // Autofocus the search box on open so typing goes straight in
      // without an extra click. Small delay lets the popover paint
      // before we grab focus.
      const t = setTimeout(() => { try { searchRef.current?.focus(); } catch { /* noop */ } }, 20);
      return () => clearTimeout(t);
    }
    setQuery('');
  }, [open]);
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const label =
    value.length === 0 ? `${placeholder}: (none)` :
    value.length === 1 ? `${placeholder}: ${value[0]}` :
                         `${placeholder}: ${value.length} selected`;
  const filteredOptions = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((s) => String(s).toLowerCase().includes(q));
  }, [options, query]);
  // 2026-07-21: "Select all matching KEYWORD" behaviour mirrors the
  // CustomerMultiSelect UX. When the search box is non-empty AND at
  // least 2 options match, a bulk-select button appears at the top
  // of the option list. Tapping it toggles ALL matching options at
  // once — de-selecting when every match is already selected,
  // otherwise selecting the rest.
  const trimmedQ = query.trim();
  const allMatchingSelected = filteredOptions.length > 0 && filteredOptions.every((s) => value.includes(s));
  const bulkSelectMatching = () => {
    const matchingSet = new Set(filteredOptions);
    if (allMatchingSelected) {
      onChange(value.filter((v) => !matchingSet.has(v)));
    } else {
      const next = new Set(value);
      filteredOptions.forEach((s) => next.add(s));
      onChange(Array.from(next));
    }
  };
  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="h-10 w-full rounded-lg border border-neutral-200 px-3 text-sm bg-white text-left flex items-center justify-between hover:border-[#0078D4]/50 transition-colors"
      >
        <span className={`truncate ${value.length ? 'text-[#0F172A] font-medium' : 'text-neutral-500'}`}>{label}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute z-40 mt-1 w-full max-h-72 rounded-lg border border-neutral-200 bg-white shadow-lg flex flex-col"
          data-testid={`${testId}-menu`}
        >
          {/* Sticky search input at the top of the popover — type to
              filter the option list. Case-insensitive substring match
              on the option text itself. */}
          <div className="p-1.5 border-b border-neutral-100 sticky top-0 bg-white rounded-t-lg">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter…"
                className="w-full h-8 pl-7 pr-2 rounded-md border border-neutral-200 text-xs focus:outline-none focus:border-[#0078D4] focus:ring-1 focus:ring-[#0078D4]/30"
                data-testid={`${testId}-search`}
                aria-label="Filter options by name"
              />
            </div>
          </div>
          <div className="overflow-y-auto p-1 flex-1">
            {(options || []).length === 0 ? (
              <div className="px-3 py-2 text-xs text-neutral-500 italic">No options available</div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-neutral-500 italic">No matches for &ldquo;{query}&rdquo;</div>
            ) : (
              <>
                {value.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="w-full text-left text-xs text-[#DC2626] hover:bg-[#FEF2F2] px-3 py-1.5 rounded"
                    data-testid={`${testId}-clear`}
                  >
                    Clear all ({value.length})
                  </button>
                )}
                {trimmedQ && filteredOptions.length >= 2 && (
                  <button
                    type="button"
                    onClick={bulkSelectMatching}
                    className="w-full text-left text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9]/60 px-3 py-1.5 rounded flex items-center gap-1.5 border-b border-neutral-100"
                    data-testid={`${testId}-select-all-matching`}
                  >
                    <span>{allMatchingSelected ? '\u2715' : '\u2714'}</span>
                    <span>
                      {allMatchingSelected ? 'Deselect all matching: ' : 'Select all matching: '}
                      <span className="font-normal italic">&ldquo;{trimmedQ}&rdquo;</span>
                      <span className="text-neutral-400 ml-1">({filteredOptions.length})</span>
                    </span>
                  </button>
                )}
                {filteredOptions.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-neutral-50 cursor-pointer text-sm"
                    data-testid={`${testId}-opt-${s}`}
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(s)}
                      onChange={() => toggle(s)}
                      className="accent-[#0078D4]"
                    />
                    <span className="truncate">{s}</span>
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const fmtDate = (iso) => formatDateTime(iso);

const AdminOutstanding = () => {
  const { token } = useAdminAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({});
  const [totals, setTotals] = useState({});
  // Server-side per-division totals (Feb 2026). Formerly derived
  // client-side from the current 500-row page slice; now sourced from
  // `/admin/outstanding/(latest|snapshots/:id)` so the tiles reflect
  // the ENTIRE filtered dataset and match the "Total Outstanding" KPI.
  const [totalsByCategory, setTotalsByCategory] = useState([]);
  // Multi-currency (Stage 1 — display only, Feb 2026). `divisionCurrencies`
  // is the slug → currency-code map (server-persisted, super-admin
  // editable). `singleCurrency` is the currency to render on aggregate
  // KPI cards when every visible division shares one currency
  // (e.g. a UAE-only share link renders AED throughout); `null` when
  // multi-currency, in which case the KPI cards fall back to OMR.
  // Stage 2 (Feb 2026): `aedToOmrRate` — server-side FX rate applied
  // at INGESTION. Storage is always OMR; the UAE-only tile / rows
  // reverse-convert (÷ rate) at display time so the UAE team sees
  // its numbers in AED while the grand-total KPI stays in OMR.
  // Stage 3 (Feb 2026): `exchangeRates` — full currency → OMR rate
  // table used to reverse-convert every non-OMR division at display
  // time. `supportedCurrencies` drives the admin dropdowns so we
  // never hard-code the picker.
  const [divisionCurrencies, setDivisionCurrencies] = useState({});
  const [singleCurrency, setSingleCurrency] = useState(null);
  const [aedToOmrRate, setAedToOmrRate] = useState(1.0);
  const [exchangeRates, setExchangeRates] = useState({});
  const [supportedCurrencies, setSupportedCurrencies] = useState([
    { code: 'OMR', name: 'Omani Rial' },
    { code: 'AED', name: 'UAE Dirham' },
  ]);
  // Directory of every unique customer_code in the snapshot with
  // per-category outstanding breakdown. Powers the customer multi-select
  // (single entry per customer regardless of division) AND the
  // division-tile retile when a customer is picked.
  const [customerDirectory, setCustomerDirectory] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  // Pagination — server returns a `total_count` + `page` + `page_size`
  // on both /admin/outstanding/latest and /admin/outstanding/snapshots/{id}
  // so we can render "showing 500 / 4123 rows" and keep the KPI cards
  // pinned to server-side aggregation numbers instead of client-side sums.
  // Default page_size mirrors the backend default (500).
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 500;

  // Super-admin gate for the "Clean duplicate records" tool. Fetched
  // from `/api/admin/me` on mount; the button is entirely hidden for
  // non-super admins. Same gate the backend enforces on
  // `/admin/outstanding/cleanup-duplicate-codes`, so a hidden UI + 403
  // API check is defence-in-depth.
  const [isSuper, setIsSuper] = useState(false);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  // Per-division compact tiles — visibility toggles are super-admin
  // configurable and independently control the admin dashboard vs the
  // public share-link view. Defaults to True so the tiles appear until
  // the super-admin explicitly hides them. Persisted in
  // `settings.outstanding.show_division_tiles_admin` /
  // `settings.outstanding.show_division_tiles_public`.
  const [showDivTilesAdmin, setShowDivTilesAdmin] = useState(true);
  const [showDivTilesPublic, setShowDivTilesPublic] = useState(true);
  const [savingDivToggle, setSavingDivToggle] = useState(false);
  // Feb 2026: Outstanding Report maintenance kill-switch (super-admin
  // only). When ON, every share-link viewer sees a branded "Under
  // Maintenance" screen instead of the report. Persisted in
  // `settings.outstanding.maintenance_mode`.
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  // Cached ticker speed so the toggle PUT can round-trip it without
  // resetting the marquee cadence (the backend $sets the whole
  // `outstanding` subdoc, so every PUT must include all persisted keys).
  const cfgTickerSpeedRef = useRef(90);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/outstanding/config`, { headers: authHeaders })
      .then(({ data }) => {
        if (!alive) return;
        if (data?.show_division_tiles_admin !== undefined) setShowDivTilesAdmin(!!data.show_division_tiles_admin);
        if (data?.show_division_tiles_public !== undefined) setShowDivTilesPublic(!!data.show_division_tiles_public);
        if (data?.ticker_speed_seconds) cfgTickerSpeedRef.current = Number(data.ticker_speed_seconds);
        if (data?.maintenance_mode !== undefined) setMaintenanceMode(!!data.maintenance_mode);
      })
      .catch(() => { /* non-fatal — defaults win */ });
    return () => { alive = false; };
  }, [authHeaders]);

  const saveDivisionTilesToggle = async (nextAdmin, nextPublic) => {
    setSavingDivToggle(true);
    try {
      await axios.put(`${API}/admin/outstanding/config`, {
        ticker_speed_seconds: Number(cfgTickerSpeedRef.current) || 90,
        show_division_tiles_admin: !!nextAdmin,
        show_division_tiles_public: !!nextPublic,
        maintenance_mode: !!maintenanceMode,
      }, { headers: authHeaders });
      toast({
        title: 'Division tiles updated',
        description: `Admin: ${nextAdmin ? 'shown' : 'hidden'} · Public: ${nextPublic ? 'shown' : 'hidden'}`,
      });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSavingDivToggle(false); }
  };

  const saveMaintenanceMode = async (next) => {
    const prev = maintenanceMode;
    setMaintenanceMode(next); // optimistic
    setSavingMaintenance(true);
    try {
      await axios.put(`${API}/admin/outstanding/config`, {
        ticker_speed_seconds: Number(cfgTickerSpeedRef.current) || 90,
        show_division_tiles_admin: !!showDivTilesAdmin,
        show_division_tiles_public: !!showDivTilesPublic,
        maintenance_mode: !!next,
      }, { headers: authHeaders });
      toast({
        title: next ? 'Maintenance mode ON' : 'Maintenance mode OFF',
        description: next
          ? 'Every visitor to the Outstanding Report now sees the maintenance screen.'
          : 'The Outstanding Report is live again.',
      });
    } catch (e) {
      setMaintenanceMode(prev);
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSavingMaintenance(false); }
  };

  // Multi-currency Stage 1 (Feb 2026) — super-admin editor for the
  // division→currency map. Purely display-driven: nothing about the
  // calculation logic changes when this map is edited, only the
  // symbol printed next to each tile / row / KPI card.
  const [savingCurrencies, setSavingCurrencies] = useState(false);
  const [currenciesOpen, setCurrenciesOpen] = useState(false);
  const [currenciesDraft, setCurrenciesDraft] = useState({});
  useEffect(() => {
    // Seed the editor draft from the server-supplied map whenever it
    // refreshes, but preserve any in-flight edits (super-admin might
    // be mid-typing when a snapshot load lands).
    setCurrenciesDraft((prev) => {
      const next = { ...(divisionCurrencies || {}) };
      Object.entries(prev || {}).forEach(([k, v]) => {
        if (v && v !== next[k]) next[k] = v;
      });
      return next;
    });
  }, [divisionCurrencies]);

  const saveDivisionCurrencies = async () => {
    setSavingCurrencies(true);
    try {
      const payload = {};
      Object.entries(currenciesDraft || {}).forEach(([k, v]) => {
        const slug = String(k || '').trim().toLowerCase();
        const code = String(v || '').trim().toUpperCase();
        if (slug && code) payload[slug] = code;
      });
      const r = await axios.put(`${API}/admin/outstanding/division-currencies`,
        { division_currencies: payload }, { headers: authHeaders });
      if (r.data?.division_currencies) setDivisionCurrencies(r.data.division_currencies);
      toast({ title: 'Currency map saved', description: 'Tiles, tables and PDF exports will use the new symbols.' });
      setCurrenciesOpen(false);
      // Stage 3 — live refresh so tiles + KPIs pick up the new
      // division→currency assignments immediately.
      try { await loadLatest(); } catch (_e) {}
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSavingCurrencies(false); }
  };

  // Multi-currency Stage 3 (Feb 2026) — super-admin editor for the
  // currency → OMR rate map. Every non-OMR division's stored OMR
  // figure is reverse-converted at display via this table so tiles
  // show native currency and the grand-total KPI stays in OMR.
  const [savingRates, setSavingRates] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [ratesDraft, setRatesDraft] = useState({});
  useEffect(() => {
    // Hydrate supported currencies + current rate table on first load
    // (independent of `load()` so the editor is populated even before
    // the report has any rows).
    axios.get(`${API}/admin/outstanding/exchange-rates`, { headers: authHeaders })
      .then((r) => {
        if (r.data?.exchange_rates && typeof r.data.exchange_rates === 'object') {
          setExchangeRates(r.data.exchange_rates);
          setRatesDraft(r.data.exchange_rates);
        }
        if (Array.isArray(r.data?.supported_currencies) && r.data.supported_currencies.length > 0) {
          setSupportedCurrencies(r.data.supported_currencies);
        }
      })
      .catch(() => { /* non-fatal — panel just stays with defaults */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Sync the rate editor draft with any server-side refresh, but
    // preserve in-flight edits.
    setRatesDraft((prev) => {
      const next = { ...(exchangeRates || {}) };
      Object.entries(prev || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== next[k]) next[k] = v;
      });
      return next;
    });
  }, [exchangeRates]);

  const saveExchangeRates = async () => {
    setSavingRates(true);
    try {
      const payload = {};
      Object.entries(ratesDraft || {}).forEach(([k, v]) => {
        const code = String(k || '').trim().toUpperCase();
        const num = Number(v);
        if (code && Number.isFinite(num) && num > 0) payload[code] = num;
      });
      // OMR is always identity — the backend pins it too but sending
      // it keeps the payload explicit.
      payload['OMR'] = 1.0;
      const r = await axios.put(`${API}/admin/outstanding/exchange-rates`,
        { exchange_rates: payload }, { headers: authHeaders });
      if (r.data?.exchange_rates) {
        setExchangeRates(r.data.exchange_rates);
        setRatesDraft(r.data.exchange_rates);
        // Keep the legacy Stage-2 state in lock-step so any tiles
        // still reading `aedToOmrRate` reverse-convert with the
        // freshly-saved AED rate.
        if (typeof r.data.exchange_rates.AED === 'number') {
          setAedToOmrRate(r.data.exchange_rates.AED);
        }
      }
      toast({
        title: 'Exchange rates saved',
        description: 'Division tiles and the Total Outstanding KPI have refreshed.',
      });
      setRatesOpen(false);
      // Stage 3 — live refresh so KPI + tiles apply the new rates.
      try { await loadLatest(); } catch (_e) {}
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSavingRates(false); }
  };

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/me`, { headers: authHeaders })
      .then((r) => { if (alive) setIsSuper(!!r.data?.is_super_admin); })
      .catch(() => { /* non-fatal — button just stays hidden */ });
    return () => { alive = false; };
  }, [authHeaders]);

  // Feb 2026 — Super-admin share-link bypass. Mints the signed
  // `mr_stock_admin` cookie on this browser so that when the super
  // admin opens ANY public Outstanding share link (even while
  // Maintenance Mode is ON) he sails straight through — no 503
  // screen, no approval gate. Matches the same one-shot mint the
  // AdminStock page performs so a single admin sign-in unlocks both
  // report surfaces.
  useEffect(() => {
    if (!isSuper) return;
    axios.post(`${API}/admin/stock/access/self-bypass`, {}, { headers: authHeaders }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const runCleanupDuplicates = async () => {
    setCleanupRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/outstanding/cleanup-duplicate-codes`,
        {},
        { headers: authHeaders },
      );
      const removed = Number(data?.removed || 0);
      const scanned = Number(data?.scanned || 0);
      const snapshots = Number(data?.snapshots_touched || 0);
      toast({
        title: removed > 0 ? 'Cleanup complete' : 'Nothing to clean',
        description: removed > 0
          ? `Removed ${removed.toLocaleString()} duplicate row${removed === 1 ? '' : 's'} across ${snapshots} snapshot${snapshots === 1 ? '' : 's'} (scanned ${scanned.toLocaleString()}).`
          : `Scanned ${scanned.toLocaleString()} rows — every customer_code was already unique per snapshot.`,
      });
      setCleanupConfirmOpen(false);
      // Refresh the visible snapshot so the row_count / totals reflect
      // the just-cleaned state.
      loadLatest();
      loadSnapshots();
    } catch (e) {
      toast({
        title: 'Cleanup failed',
        description: e.response?.data?.detail || 'Try again in a moment.',
      });
    } finally {
      setCleanupRunning(false);
    }
  };

  // Reconciliation audit — fetches every source of divergence between
  // the source Excel's SUM(OS Amt) and the app's KPI figure so the
  // super-admin can pinpoint exactly which rows contribute to any
  // overshoot/undershoot. Read-only, no data mutation.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);

  const openReconcile = async () => {
    setReconcileOpen(true);
    setReconcileLoading(true);
    setReconcileData(null);
    try {
      const { data } = await axios.get(`${API}/admin/outstanding/reconcile`, { headers: authHeaders });
      setReconcileData(data);
    } catch (e) {
      toast({
        title: 'Reconciliation failed',
        description: e.response?.data?.detail || 'Try again in a moment.',
      });
      setReconcileOpen(false);
    } finally { setReconcileLoading(false); }
  };

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Debounce the search — with 4k+ rows every keystroke otherwise triggers
  // a fresh filter/sort/render pass.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);
  // 2026-07-21: salesman is now MULTI-select (empty array = All).
  // Same UX as the customer filter — searchable, multi-pick,
  // "Select all matching KEYWORD" bulk action available in the popover.
  const [salesmen, setSalesmen] = useState([]);
  // 2026-07-21: dept multi-select filter added to the filter bar
  // alongside the salesman + division dropdowns.
  const [depts, setDepts] = useState([]);
  const [division, setDivision] = useState('__all__');
  const [custType, setCustType] = useState('__all__');
  const [creditType, setCreditType] = useState('__all__');
  const [category, setCategory] = useState('__all__');
  const [agingBucket, setAgingBucket] = useState('__all__'); // "curr", "b30" ... "over181"
  // Multi-customer filter (Feb 2026): searchable multi-select that
  // narrows KPIs + table to only the picked customer_codes. Empty
  // array = no filter (full dataset).
  const [customerCodes, setCustomerCodes] = useState([]);
  // Feb 2026 (July) — keyword provenance for the customer picker.
  // Non-empty ONLY when the picker's "Select all matching KEYWORD"
  // bulk action was the last thing the user did. Cleared automatically
  // on any manual toggle / clear. Passed to `exportFile` so the PDF
  // filter summary reads `Customers: KEYWORD (N)` instead of the plain
  // count.
  const [customerKeyword, setCustomerKeyword] = useState('');
  const [sortKey, setSortKey] = useState('total_os');
  const [sortDir, setSortDir] = useState('desc');

  const [visibleCols, setVisibleCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_COL_KEY)) || COLS.map((c) => c.key); }
    catch { return COLS.map((c) => c.key); }
  });
  const [showColMenu, setShowColMenu] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  // Salesman-wise email subscription modal state (Feb 2026 feature).
  const [salesmanRecipientsOpen, setSalesmanRecipientsOpen] = useState(false);
  // Manual recipients — third parallel outstanding-email pipeline (Feb 2026).
  const [manualRecipientsOpen, setManualRecipientsOpen] = useState(false);
  // Row cap — with 4k+ rows the browser lags rendering the full table.
  // Users start with 200 rows and can click "Show all" if they need more.
  const [rowLimit, setRowLimit] = useState(200);
  const [ticker, setTicker] = useState([]);

  // Bulk-edit-category on Outstanding rows. Selection is keyed by
  // customer_code so it survives search/filter changes — the backend
  // endpoint takes customer_codes directly and updates both the master
  // customer_profiles.category AND the current snapshot's outstanding_rows
  // in one call so the heatmap refreshes without needing a reload.
  const [categories, setCategories] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState(() => new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem(LS_COL_KEY, JSON.stringify(visibleCols));
  }, [visibleCols]);

  const loadLatest = async () => {
    setLoading(true);
    try {
      // Feb 2026 (LULU 78-row cap fix): push the customer_codes
      // multi-select into the server query so pagination doesn't cut
      // off matches. Without this, only the LULU codes that happen to
      // land in the first `pageSize` rows survive the client-side
      // filter — the rest get silently dropped.
      //
      // 2026-08 — salesman + dept multi-selects are also pushed server-
      // side so KPI tiles, division tiles and the row table all update
      // to the picked slice. Previously only the client-side row
      // filter narrowed data — the server-computed `totals` +
      // `totals_by_category` stayed at the full-snapshot values,
      // making the KPIs appear frozen when a salesman was picked.
      const params = { page: 1, page_size: pageSize };
      if (customerCodes.length > 0) params.customer_codes = customerCodes;
      if (salesmen.length > 0) params.salesman = salesmen;
      if (depts.length > 0) params.dept = depts;
      // Oct 2026 — Push the single-select facets (division/type/credit/
      // category) into the server call so `totals`, `totals_by_category`,
      // `single_currency` + `exchange_rates` reflect the narrowed scope.
      // Previously these were applied in-browser only, which kept the
      // KPI tiles frozen on the full-snapshot aggregate and left
      // `single_currency = null` even when the viewer had drilled into
      // a UAE-only slice.
      if (division && division !== '__all__') params.division = division;
      if (custType && custType !== '__all__') params.cust_type = custType;
      if (creditType && creditType !== '__all__') params.credit_type = creditType;
      if (category && category !== '__all__') params.category = category;
      const res = await axios.get(`${API}/admin/outstanding/latest`, {
        headers: authHeaders,
        params,
        paramsSerializer: (p) => {
          // Repeat `customer_codes=` for each value so FastAPI's
          // `q.getlist("customer_codes")` picks them all up.
          const sp = new URLSearchParams();
          Object.entries(p).forEach(([k, v]) => {
            if (Array.isArray(v)) v.forEach((x) => sp.append(k, x));
            else if (v !== undefined && v !== null) sp.set(k, v);
          });
          return sp.toString();
        },
      });
      setSnapshot(res.data.snapshot);
      setRows(res.data.rows || []);
      setFacets(res.data.facets || {});
      setTotals(res.data.totals || {});
      setTotalsByCategory(res.data.totals_by_category || []);
      setCustomerDirectory(res.data.customer_directory || []);
      if (res.data.division_currencies) setDivisionCurrencies(res.data.division_currencies);
      setSingleCurrency(res.data.single_currency || null);
      if (res.data.aed_to_omr_rate !== undefined && res.data.aed_to_omr_rate !== null) {
        setAedToOmrRate(Number(res.data.aed_to_omr_rate) || 1.0);
      }
      if (res.data.exchange_rates && typeof res.data.exchange_rates === 'object') {
        setExchangeRates(res.data.exchange_rates);
      }
      setTotalCount(Number(res.data.total_count) || (res.data.rows || []).length);
      setPage(Number(res.data.page) || 1);
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };

  const loadSnapshots = async () => {
    try {
      const r = await axios.get(`${API}/admin/outstanding/snapshots`, { headers: authHeaders });
      setSnapshots(r.data || []);
    } catch { /* silent */ }
  };

  const loadSnapshotById = async (id) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/outstanding/snapshots/${id}`, {
        headers: authHeaders,
        params: { page: 1, page_size: pageSize },
      });
      setSnapshot(r.data.snapshot);
      setRows(r.data.rows || []);
      setFacets(r.data.facets || {});
      setTotals(r.data.totals || {});
      setTotalsByCategory(r.data.totals_by_category || []);
      setCustomerDirectory(r.data.customer_directory || []);
      if (r.data.division_currencies) setDivisionCurrencies(r.data.division_currencies);
      setSingleCurrency(r.data.single_currency || null);
      if (r.data.aed_to_omr_rate !== undefined && r.data.aed_to_omr_rate !== null) {
        setAedToOmrRate(Number(r.data.aed_to_omr_rate) || 1.0);
      }
      if (r.data.exchange_rates && typeof r.data.exchange_rates === 'object') {
        setExchangeRates(r.data.exchange_rates);
      }
      setTotalCount(Number(r.data.total_count) || (r.data.rows || []).length);
      setPage(Number(r.data.page) || 1);
      setHistoryOpen(false);
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm('Delete this snapshot permanently?')) return;
    try {
      await axios.delete(`${API}/admin/outstanding/snapshots/${id}`, { headers: authHeaders });
      toast({ title: 'Snapshot deleted' });
      await loadSnapshots();
      await loadLatest();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  useEffect(() => { loadLatest(); loadSnapshots(); }, []);

  // Feb 2026 (LULU 78-row cap fix) + 2026-08 (salesman/dept multi-select):
  // when any of the multi-select filters changes, re-fetch with the
  // picks pushed server-side so KPIs / division tiles / paginated rows
  // all reflect the same filtered slice. Debounced 250 ms so rapid
  // checkbox clicks don't trigger a network burst. Skips the initial
  // mount — the primary useEffect above handles that fetch.
  const _custCodesFirstRun = useRef(true);
  useEffect(() => {
    if (_custCodesFirstRun.current) {
      _custCodesFirstRun.current = false;
      return;
    }
    const t = setTimeout(() => { loadLatest(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(customerCodes), JSON.stringify(salesmen), JSON.stringify(depts), division, custType, creditType, category]);

  // Fetch categories once so the bulk-edit dropdown is ready.
  useEffect(() => {
    axios.get(`${API}/categories`).then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
  }, []);

  // OS Movement (2026-08) — signed delta between the current snapshot's
  // filtered os_amt total and the immediately-previous snapshot's
  // filtered os_amt total under the SAME filter set. Server-side
  // computed via /admin/outstanding/movement so both current and
  // previous slices honour salesman / dept / division / customer_codes
  // consistently (client-side filters don't apply to previous
  // snapshot rows).
  const [osMovement, setOsMovement] = useState(null);
  const [osMovementLoading, setOsMovementLoading] = useState(false);
  useEffect(() => {
    if (!snapshot?.id) { setOsMovement(null); return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setOsMovementLoading(true);
      try {
        const sp = new URLSearchParams();
        sp.set('snapshot_id', snapshot.id);
        salesmen.forEach((s) => sp.append('salesman', s));
        depts.forEach((d) => sp.append('dept', d));
        if (division !== '__all__')   sp.set('division', division);
        if (custType !== '__all__')   sp.set('cust_type', custType);
        if (creditType !== '__all__') sp.set('credit_type', creditType);
        if (category !== '__all__')   sp.set('category', category);
        customerCodes.forEach((c) => sp.append('customer_codes', c));
        const { data } = await axios.get(
          `${API}/admin/outstanding/movement?${sp.toString()}`,
          { headers: authHeaders, signal: controller.signal },
        );
        setOsMovement(data);
      } catch (e) {
        if (!axios.isCancel?.(e)) { /* silent — tile just hides */ }
      } finally {
        setOsMovementLoading(false);
      }
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.id, JSON.stringify(salesmen), JSON.stringify(depts), division, custType, creditType, category, JSON.stringify(customerCodes)]);

  const exportFile = async (kind) => {
    try {
      const body = {
        q: search,
        // 2026-07-21: salesman is multi-select — send array for
        // multiple picks, scalar for single (backend accepts both).
        salesman: salesmen.length === 0 ? '' : (salesmen.length === 1 ? salesmen[0] : salesmen),
        // 2026-07-21: dept multi-select — same shape convention.
        dept: depts.length === 0 ? '' : (depts.length === 1 ? depts[0] : depts),
        division: division === '__all__' ? '' : division,
        cust_type: custType === '__all__' ? '' : custType,
        credit_type: creditType === '__all__' ? '' : creditType,
        category: category === '__all__' ? '' : category,
        aging: agingBucket === '__all__' ? '' : agingBucket,
        // Multi-select customer picker — server filters `_apply_filters`
        // honours `customer_codes: List[str]` so the exported PDF/XLSX
        // reflects the exact same slice as the on-screen table.
        customer_codes: customerCodes.length > 0 ? customerCodes : null,
        // Feb 2026 (July) — keyword provenance for the customer
        // multi-select. Backend uses this to render the PDF filter
        // summary as `Customers: KEYWORD (N)` when set.
        customer_keyword: (customerCodes.length > 0 && customerKeyword) ? customerKeyword : null,
        columns: visibleCols,
        snapshot_id: snapshot?.id,
      };
      const url = `${API}/admin/outstanding/export-${kind}`;
      const res = await axios.post(url, body, { headers: authHeaders, responseType: 'blob' });
      const objUrl = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `outstanding-${new Date().toISOString().slice(0, 10)}.${kind === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
      toast({ title: `${kind.toUpperCase()} downloaded` });
    } catch (e) {
      toast({ title: 'Export failed', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  const customerCodesSet = useMemo(() => new Set(customerCodes || []), [customerCodes]);
  // 2026-07-21: fast-lookup sets for the new multi-select facets so
  // the in-memory row filter stays O(rows) not O(rows*picks).
  const salesmenSet = useMemo(() => new Set(salesmen || []), [salesmen]);
  const deptsSet    = useMemo(() => new Set(depts || []),    [depts]);

  const filtered = useMemo(() => {
    const qRaw = debouncedSearch.trim();
    const q = qRaw.toLowerCase();
    // Aging-shortcut: typing exactly `60`, `90` or `120` in the search
    // box filters the visible rows to customers with money in that
    // aging bucket (Feb 2026 user request — the ERP's aging labels are
    // the two-digit "days overdue" thresholds, so admins can jump
    // straight to the "who's late by ≥ 60 days" slice without touching
    // the dedicated aging dropdown). The dropdown remains available
    // for the other buckets. Empty / non-shortcut queries fall through
    // to the free-text branch below.
    const AGING_SHORTCUTS = { '60': 'b61', '90': 'b91', '120': 'b122' };
    const shortcutKey = AGING_SHORTCUTS[qRaw];
    return rows.filter((r) => {
      if (shortcutKey) {
        if (!(Number(r[shortcutKey]) > 0)) return false;
      } else if (q) {
        // Free-text search — now also matches on `salesman` so admins
        // typing a salesman name find every one of that salesman's
        // customers directly from the search box.
        const hay = `${r.customer_code || ''} ${r.name || ''} ${r.salesman || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // 2026-07-21: salesman multi-select — pass row when either no
      // filter picked OR the row's salesman is in the picked set.
      if (salesmen.length > 0 && !salesmenSet.has((r.salesman || ''))) return false;
      // 2026-07-21: dept multi-select — same convention as salesman.
      if (depts.length > 0 && !deptsSet.has((r.dept || ''))) return false;
      if (division !== '__all__' && (r.division || '') !== division) return false;
      if (custType !== '__all__' && (r.cust_type || '') !== custType) return false;
      if (creditType !== '__all__' && (r.credit_type || '') !== creditType) return false;
      if (category !== '__all__' && (r.category || '') !== category) return false;
      if (agingBucket !== '__all__' && !(Number(r[agingBucket]) > 0)) return false;
      // Feb-2026: multi-customer filter — when non-empty, restrict to
      // the picked customer_codes. Set membership is O(1).
      if (customerCodes.length > 0 && !customerCodesSet.has(r.customer_code)) return false;
      return true;
    });
  }, [rows, debouncedSearch, salesmen, salesmenSet, depts, deptsSet, division, custType, creditType, category, agingBucket, customerCodes, customerCodesSet]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    arr.sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (typeof va === 'number' || typeof vb === 'number') {
        return (sortDir === 'asc' ? 1 : -1) * ((Number(va) || 0) - (Number(vb) || 0));
      }
      return (sortDir === 'asc' ? 1 : -1) * String(va || '').localeCompare(String(vb || ''));
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // KPI numbers — sourced from server-side aggregation (`totals` populated
  // by /admin/outstanding/latest) so the tiles stay correct even when the
  // current page only holds 500 of the snapshot's rows. Fallback to the
  // client-side sum on the current filtered slice preserves the previous
  // behaviour if the API ever returns without totals (older backend).
  //
  // "Total Outstanding" sums the ERP-emitted `os_amt` column directly
  // (Feb 2026 user request) — previously we summed the derived `total_os`
  // (= sum of aging buckets, falling back to os_amt only when buckets
  // were blank). The two only agree when the ERP export includes every
  // aging bucket; on partial exports (or when the ERP net-of-PDC column
  // is what accountants actually reconcile against) `os_amt` is the
  // authoritative value.
  // Oct 2026 — per-row OMR multiplier for client-side totals.
  // When client-only filters are active (customer_codes picker,
  // residual q/aging), KPI and table totals are summed from the
  // `filtered` slice instead of the server-side aggregation; this
  // helper mirrors the backend's `_row_omr_multiplier` so the
  // numbers stay reconciled with the Total Outstanding card.
  // Generalised to every non-OMR code (AED, SAR, …) by reading
  // the full `exchangeRates` map; the legacy `aedToOmrRate`
  // scalar is only consulted when AED is missing from the table.
  const rowOmrMultiplier = useCallback((row) => {
    // Feb 2026 double-AED fix. Rows stamped at ingestion / backfill
    // are already in OMR — the server copies `_fx_converted_at_ingestion`
    // and `aed_to_omr_backfill` into the deduped cache so the row we
    // read here carries the markers. Multiplying again would deflate
    // the figure by (rate / 1), e.g. 100 AED stored as 10.47 OMR
    // would re-emerge as 1.096 OMR. Marker branch returns 1.
    if (row?._fx_converted_at_ingestion === true || row?.aed_to_omr_backfill === true) {
      return 1;
    }
    const slug = String(row?.category || '').toLowerCase();
    const cur = (divisionCurrencies && divisionCurrencies[slug]) || 'OMR';
    const curU = String(cur).toUpperCase();
    if (!curU || curU === 'OMR') return 1;
    const raw = Number(exchangeRates?.[curU]);
    if (Number.isFinite(raw) && raw > 0) return raw;
    if (curU === 'AED') {
      const legacy = Number(aedToOmrRate);
      if (Number.isFinite(legacy) && legacy > 0) return legacy;
    }
    return 1;
  }, [divisionCurrencies, exchangeRates, aedToOmrRate]);

  const kpis = useMemo(() => {
    const t = totals || {};
    const hasServerTotals = typeof t.os_amt === 'number';
    // Feb-2026: multi-customer filter is a CLIENT-only slice (server
    // totals reflect the full dataset), so when any customer_codes are
    // picked we must aggregate over `filtered` client-side — server
    // totals would over-count everyone else.
    const clientOnlyFilterActive = customerCodes.length > 0;
    // Feb 2026 user request: when an aging bucket is selected, expose
    // a single `agingBucketTotal` matching the sum of that bucket over
    // the ENTIRE filtered set. Uses server totals when available so
    // pagination doesn't distort the figure; falls back to client-side
    // row sum on the current page slice otherwise.
    let agingBucketTotal = 0;
    if (agingBucket !== '__all__') {
      if (hasServerTotals && t[agingBucket] != null) {
        agingBucketTotal = Number(t[agingBucket]) || 0;
      } else {
        agingBucketTotal = filtered.reduce(
          (s, r) => s + (Number(r[agingBucket]) || 0) * rowOmrMultiplier(r),
          0,
        );
      }
    }
    if (hasServerTotals && !clientOnlyFilterActive) {
      const overdue = (Number(t.b122) || 0) + (Number(t.b150) || 0) + (Number(t.b181) || 0) + (Number(t.over181) || 0);
      return {
        totalOs: Number(t.os_amt) || 0,
        totalPdc: Number(t.pdc_amt) || 0,
        overdue,
        // "Total Cheques Return" — sums the DN Amt column across the
        // currently-filtered dataset. Because the server-side
        // aggregation applies the same `mongo_q` as the KPI totals,
        // a division/salesman/type filter narrows this figure to the
        // matching rows only (Feb 2026 user request replacing the
        // "Customers w/ OS" count tile).
        dnAmt: Number(t.dn_amt) || 0,
        // "Total Uncovered" — sums the ERP `uncovered` column (Feb
        // 2026 user request). Same server-side aggregation path so
        // filters narrow it identically.
        uncov: Number(t.uncovered) || 0,
        agingBucketTotal,
      };
    }
    // Client-side fallback — multiply each non-OMR row by its live
    // OMR rate so the KPI figure matches the backend aggregation.
    const overdue = filtered.reduce((s, r) => {
      const m = rowOmrMultiplier(r);
      return s
        + (Number(r.b122) || 0) * m
        + (Number(r.b150) || 0) * m
        + (Number(r.b181) || 0) * m
        + (Number(r.over181) || 0) * m;
    }, 0);
    const totalOs = filtered.reduce((s, r) => s + (Number(r.os_amt) || 0) * rowOmrMultiplier(r), 0);
    const totalPdc = filtered.reduce((s, r) => s + (Number(r.pdc_amt) || 0) * rowOmrMultiplier(r), 0);
    const dnAmt = filtered.reduce((s, r) => s + (Number(r.dn_amt) || 0) * rowOmrMultiplier(r), 0);
    const uncov = filtered.reduce((s, r) => s + (Number(r.uncovered) || 0) * rowOmrMultiplier(r), 0);
    return { totalOs, totalPdc, overdue, dnAmt, uncov, agingBucketTotal };
  }, [totals, filtered, agingBucket, customerCodes, rowOmrMultiplier]);

  const filteredTotals = useMemo(() => {
    const t = { curr: 0, b30: 0, b61: 0, b91: 0, b122: 0, b150: 0, b181: 0, over181: 0, total_os: 0, os_amt: 0, dn_amt: 0, pdc_amt: 0, uncovered: 0 };
    filtered.forEach((r) => {
      const m = rowOmrMultiplier(r);
      Object.keys(t).forEach((k) => { t[k] += (Number(r[k]) || 0) * m; });
    });
    return t;
  }, [filtered, rowOmrMultiplier]);

  // Oct 2026 — Native-currency re-render for single-non-OMR scope.
  // When the filter narrows every visible row to one non-OMR
  // currency AND that currency has a usable rate configured, the
  // KPI tiles divide the OMR aggregate back into the native
  // currency so the headline matches the division tile and the
  // row cells beneath it. Multi-currency and unfiltered views
  // stay in OMR (divisor stays 1).
  const kpiNativeDivisor = useMemo(() => {
    if (!singleCurrency) return 1;
    const code = String(singleCurrency).toUpperCase();
    if (!code || code === 'OMR') return 1;
    const raw = Number(exchangeRates?.[code]);
    if (Number.isFinite(raw) && raw > 0) return raw;
    if (code === 'AED') {
      const legacy = Number(aedToOmrRate);
      if (Number.isFinite(legacy) && legacy > 0) return legacy;
    }
    return 1;
  }, [singleCurrency, exchangeRates, aedToOmrRate]);
  // Convenience helper — reads `undefined` as 0 and divides only
  // when the scope resolves to a native divisor. Keeps JSX terse.
  const kpiNative = (v) => (kpiNativeDivisor && kpiNativeDivisor !== 1)
    ? (Number(v || 0) / kpiNativeDivisor)
    : Number(v || 0);

  const toggleCol = (k) => {
    setVisibleCols((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };
  const setAllCols = (on) => {
    setVisibleCols(on ? COLS.map((c) => c.key) : COLS.filter((c) => c.alwaysOn).map((c) => c.key));
  };

  const activeCols = useMemo(
    () => {
      // Feb 2026 user request: when an aging bucket is selected, hide
      // every OTHER aging column so the table zooms in on the one
      // bucket. Non-aging columns (Cust, Customer, DN Amt, etc.) are
      // untouched. "All aging" restores the previous full-column set
      // (subject to the user's manual Columns menu overrides).
      const base = COLS.filter((c) => c.alwaysOn || visibleCols.includes(c.key));
      if (agingBucket === '__all__') return base;
      return base.filter((c) => !AGING_KEYS.includes(c.key) || c.key === agingBucket);
    },
    [visibleCols, agingBucket],
  );

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const resetFilters = () => {
    setSearch(''); setSalesmen([]); setDepts([]); setDivision('__all__'); setCustType('__all__');
    setCreditType('__all__'); setCategory('__all__'); setAgingBucket('__all__');
    setCustomerCodes([]);
    setCustomerKeyword('');
  };

  // ---- Bulk-edit-category helpers (Outstanding page) ----
  const categoryLabel = (slug) => {
    if (!slug) return '';
    const hit = categories.find((c) => c.slug === slug);
    return hit?.name || slug;
  };

  const toggleSelectCode = (code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selectedCodes.has(r.customer_code));
  const someFilteredSelected = filtered.some((r) => selectedCodes.has(r.customer_code));

  const toggleSelectAllFiltered = () => {
    setSelectedCodes((prev) => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        filtered.forEach((r) => next.delete(r.customer_code));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach((r) => next.add(r.customer_code));
      return next;
    });
  };

  const clearSelection = () => setSelectedCodes(new Set());

  const applyBulkCategory = async (mode /* 'assign' | 'clear' */) => {
    const codes = Array.from(selectedCodes);
    if (codes.length === 0) {
      toast({ title: 'No rows selected' });
      return;
    }
    const slug = mode === 'clear' ? '' : (bulkCategory || '').trim();
    if (mode === 'assign' && !slug) {
      toast({ title: 'Pick a division first' });
      return;
    }
    const label = slug ? categoryLabel(slug) : 'no category';
    if (!window.confirm(
      `${mode === 'clear' ? 'Clear category on' : `Assign ${label} to`} ${codes.length} customer(s)?\n\nThis updates both the customer master and the current outstanding snapshot.`,
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/bulk-edit-category`,
        { customer_codes: codes, category: slug },
        { headers: authHeaders },
      );
      // Optimistically patch the rows in state so the heatmap +
      // "Outstanding by category" tile refresh instantly.
      setRows((list) => list.map((r) => (selectedCodes.has(r.customer_code) ? { ...r, category: slug } : r)));
      toast({
        title: mode === 'clear' ? 'Category cleared' : 'Category assigned',
        description: `${data.updated || 0} customer profile(s) updated · ${data.outstanding_rows_updated || 0} row(s) refreshed${slug ? ` → ${label}` : ''}`,
      });
      clearSelection();
    } catch (err) {
      toast({ title: 'Bulk update failed', description: err?.response?.data?.detail || err?.message || '' });
    } finally {
      setBulkBusy(false);
    }
  };

  // Per-category OS totals — sourced from server-side aggregation
  // (`totalsByCategory`). Since Feb 2026, when a customer multi-select
  // is applied the frontend re-fetches and the server narrows
  // `totals_by_category` to just the picked customers via the
  // same `mongo_q` used for the rows/totals. So the tiles below
  // are always in sync with whatever the picker + facets currently
  // constrain — no client-side recompute needed.
  //
  // Fallback 1 (`customerDirectory.per_category`) — used ONLY when
  // the server tiles come back empty AND customers are picked (older
  // backend without server-side customer_codes push, or a race where
  // the refetch hasn't landed yet). Guaranteed to produce sensible
  // tiles from the aggregate directory alone.
  //
  // Fallback 2 (`filtered`) — page-slice sum, used only when both
  // above are empty. Keeps the tile row from going silent on a
  // deploy-skew edge case.
  const byCategory = useMemo(() => {
    if (Array.isArray(totalsByCategory) && totalsByCategory.length > 0) {
      return totalsByCategory
        .filter((t) => Number(t.os_amt) > 0)
        .map((t) => ({
          slug: String(t.category || '').toLowerCase(),
          label: t.label || (t.category || '— unassigned'),
          amount: Number(t.os_amt) || 0,
        }));
    }
    // Fallback 1 — reconstruct from directory when server tiles are
    // empty. Case-insensitive match on customer_code so a DB with
    // mixed casing doesn't silently skip every entry.
    if (customerCodes.length > 0 && customerDirectory.length > 0) {
      const picked = new Set(customerCodes.map((c) => String(c || '').trim().toLowerCase()));
      const agg = {};
      customerDirectory.forEach((c) => {
        const code = String(c.customer_code || '').trim().toLowerCase();
        if (!picked.has(code)) return;
        const pc = c.per_category || {};
        Object.entries(pc).forEach(([slug, amt]) => {
          const key = slug === '__unassigned__' ? '' : slug;
          agg[key] = (agg[key] || 0) + (Number(amt) || 0);
        });
      });
      const out = Object.entries(agg)
        .filter(([, amount]) => amount > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([slug, amount]) => ({
          slug,
          label: slug
            ? (slug.length <= 4 && !slug.includes('-') && /^[a-z]+$/.test(slug)
                ? slug.toUpperCase()                       // UAE / KSA / USA
                : slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '))
            : '— unassigned',
          amount,
        }));
      if (out.length > 0) return out;
    }
    const out = {};
    filtered.forEach((r) => {
      const k = r.category || '__unassigned__';
      out[k] = (out[k] || 0) + (Number(r.os_amt) || 0);
    });
    return Object.entries(out)
      .filter(([, amount]) => amount > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([slug, amount]) => ({
        slug: slug === '__unassigned__' ? '' : slug,
        label: slug === '__unassigned__' ? '— unassigned' : slug,
        amount,
      }));
  }, [totalsByCategory, filtered, customerCodes, customerDirectory]);

  // Palette keyed to slug so Chips is always warm-orange, Water always
  // teal, etc. Unknown slugs fall back to slate. Mirrors the palette in
  // the public share-link view for a consistent brand identity.
  const CAT_PALETTE = {
    'chips':     { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
    'wafers':    { bg: '#FCE7F3', fg: '#9D174D', dot: '#DB2777' },
    'ice-cream': { bg: '#DBEAFE', fg: '#1E3A8A', dot: '#2563EB' },
    'water':     { bg: '#CFFAFE', fg: '#155E75', dot: '#0891B2' },
    'frozen':    { bg: '#E0E7FF', fg: '#3730A3', dot: '#4F46E5' },
  };
  const catStyle = (slug) => CAT_PALETTE[slug] || { bg: '#F1F5F9', fg: '#334155', dot: '#64748B' };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight">Customer Outstanding</h1>
            <p className="text-sm text-neutral-500 mt-1">
              AR aging analysis by customer.
              {snapshot?.snapshot_at && (
                <span className="ml-2 text-[#0078D4] font-medium">
                  Snapshot: {fmtDate(snapshot.snapshot_at)} · {snapshot.uploaded_by} · {snapshot.row_count} rows
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => { loadLatest(); loadSnapshots(); }} data-testid="outstanding-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" onClick={() => exportFile('xlsx')} disabled={!snapshot} data-testid="outstanding-export-xlsx-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" onClick={() => exportFile('pdf')} disabled={!snapshot} data-testid="outstanding-export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button variant="outline" onClick={() => setLinksOpen(true)} data-testid="outstanding-share-links-btn">
              <Link2 className="w-4 h-4 mr-2" /> Share links
            </Button>
            <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={!snapshot} data-testid="outstanding-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Email report
            </Button>
            <Button variant="outline" onClick={() => setSubsOpen(true)} data-testid="outstanding-subs-btn">
              <Bell className="w-4 h-4 mr-2" /> Subscribers
            </Button>
            <Button variant="outline" onClick={() => setSalesmanRecipientsOpen(true)} data-testid="outstanding-salesman-recipients-btn">
              <Mail className="w-4 h-4 mr-2" /> Salesman emails
            </Button>
            <Button variant="outline" onClick={() => setManualRecipientsOpen(true)} data-testid="outstanding-manual-recipients-btn">
              <Mail className="w-4 h-4 mr-2" /> Manual recipients
            </Button>
            <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="outstanding-history-btn">
              <ChevronDown className="w-4 h-4 mr-2" /> History ({snapshots.length})
            </Button>
            <Button onClick={() => setUploadOpen(true)} className="bg-[#0078D4] hover:bg-[#005A9E] text-white" data-testid="outstanding-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload snapshot
            </Button>
            {/* Super-admin only — walks every snapshot and drops
                per-customer duplicate rows introduced by the pre-Feb-
                2026 pair-key merge logic. Gated by
                `is_super_admin` from /admin/me + a matching backend
                403 gate on the cleanup endpoint. */}
            {isSuper && (
              <Button
                variant="outline"
                onClick={() => setCleanupConfirmOpen(true)}
                data-testid="outstanding-cleanup-duplicates-btn"
                className="border-[#DC2626]/40 text-[#B91C1C] hover:bg-[#FEF2F2]"
                title="Remove duplicate customer rows introduced by legacy uploads (super-admin only)."
              >
                <ShieldAlert className="w-4 h-4 mr-2" /> Clean duplicate records
              </Button>
            )}
            {isSuper && (
              <Button
                variant="outline"
                onClick={openReconcile}
                data-testid="outstanding-reconcile-btn"
                className="border-[#0078D4]/40 text-[#0078D4] hover:bg-[#F0F9FF]"
                title="Audit the latest snapshot against the source Excel to pinpoint any OS Amt discrepancy (super-admin only)."
              >
                <AlertTriangle className="w-4 h-4 mr-2" /> Reconcile totals
              </Button>
            )}
          </div>
        </div>

        {/* Feb 2026 — Outstanding Report maintenance kill-switch.
            Super-admin only. When ON, every public share-link viewer
            sees a branded "Under Maintenance" screen instead of the
            report. Rendered here (top of the admin page) so the state
            is always in-sight when an admin lands on this screen. */}
        {isSuper && (
          <div
            className={`rounded-2xl border px-4 py-3 flex items-center gap-4 ${
              maintenanceMode
                ? 'border-amber-300 bg-amber-50'
                : 'border-neutral-200 bg-white'
            }`}
            data-testid="outstanding-maintenance-row"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <ShieldAlert className={`w-4 h-4 ${maintenanceMode ? 'text-amber-600' : 'text-neutral-400'}`} />
                <div className="text-sm font-semibold text-[#0F172A]">Outstanding Report Maintenance Mode</div>
                {maintenanceMode && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                    data-testid="outstanding-maintenance-badge-active"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> Active
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
                When ON, every viewer of the Outstanding Report share links sees a
                branded &ldquo;Under Maintenance&rdquo; screen instead of the report.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-neutral-500" data-testid="outstanding-maintenance-state-label">
                {maintenanceMode ? 'ON' : 'OFF'}
              </span>
              <Switch
                checked={maintenanceMode}
                disabled={savingMaintenance}
                onCheckedChange={saveMaintenanceMode}
                data-testid="outstanding-maintenance-toggle"
                aria-label="Outstanding Report Maintenance Mode"
              />
            </div>
          </div>
        )}

        {/* Multi-currency Stage 1 (Feb 2026). Super-admin editor for
            the division→currency map. Rendered as a collapsed row so
            the busy admin page stays uncluttered; opens into a compact
            inline editor with one input per known division slug. This
            is a display-only overlay — calculations are unchanged. */}
        {isSuper && (
          <div
            className="rounded-2xl border border-neutral-200 bg-white px-4 py-3"
            data-testid="outstanding-currency-map-row"
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0F172A]">Division currencies</div>
                <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
                  Assign a currency symbol per division. Applied on tiles, tables and PDF exports.
                  <span className="ml-1 opacity-70">Calculation logic is unchanged — this is a display-only overlay.</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(divisionCurrencies || {}).slice(0, 6).map(([slug, code]) => (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-1 rounded-full bg-neutral-50 border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-700"
                    data-testid={`outstanding-currency-chip-${slug}`}
                  >
                    <span className="uppercase font-semibold tracking-wide">{String(slug).toUpperCase()}</span>
                    <span className="text-neutral-400">·</span>
                    <span className="font-bold tabular-nums">{String(code || 'OMR').toUpperCase()}</span>
                  </span>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrenciesOpen((v) => !v)}
                  data-testid="outstanding-currency-map-toggle"
                >
                  {currenciesOpen ? 'Close' : 'Edit'}
                </Button>
              </div>
            </div>
            {currenciesOpen && (
              <div className="mt-3 pt-3 border-t border-neutral-100" data-testid="outstanding-currency-map-editor">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {Array.from(new Set([
                    ...Object.keys(divisionCurrencies || {}),
                    ...byCategory.map((x) => String(x.slug || '').toLowerCase()).filter(Boolean),
                  ])).sort().map((slug) => (
                    <label key={slug} className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5">
                      <span className="text-[11px] uppercase font-semibold tracking-wide text-neutral-500 flex-1 min-w-0 truncate">{String(slug).toUpperCase()}</span>
                      <select
                        value={String(currenciesDraft[slug] ?? divisionCurrencies[slug] ?? 'OMR').toUpperCase()}
                        onChange={(e) => {
                          const v = String(e.target.value || '').toUpperCase();
                          setCurrenciesDraft((prev) => ({ ...(prev || {}), [slug]: v }));
                        }}
                        className="w-24 text-center tabular-nums text-[12px] font-semibold border border-neutral-300 rounded px-1 py-0.5 bg-white"
                        data-testid={`outstanding-currency-input-${slug}`}
                      >
                        {supportedCurrencies.map((c) => (
                          <option key={c.code} value={c.code}>{c.code}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setCurrenciesDraft({ ...(divisionCurrencies || {}) }); setCurrenciesOpen(false); }}
                    disabled={savingCurrencies}
                    data-testid="outstanding-currency-map-cancel"
                  >Cancel</Button>
                  <Button
                    size="sm"
                    onClick={saveDivisionCurrencies}
                    disabled={savingCurrencies}
                    data-testid="outstanding-currency-map-save"
                  >{savingCurrencies ? 'Saving…' : 'Save currencies'}</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Multi-currency Stage 3 (Feb 2026). Super-admin editor for
            the currency → OMR exchange-rate table. Sits below the
            division-currency panel because rates only matter once at
            least one division has been assigned a non-OMR currency.
            Grand-total KPI + division tiles refresh immediately after
            a save. */}
        {isSuper && (
          <div
            className="rounded-2xl border border-neutral-200 bg-white px-4 py-3"
            data-testid="outstanding-fx-rates-row"
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0F172A]">Exchange rate table</div>
                <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
                  Set how many OMR each foreign currency equals (e.g. AED = 0.1047).
                  <span className="ml-1 opacity-70">Applied at ingestion + on every tile/KPI render — save to refresh instantly.</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(exchangeRates || {})
                  .filter(([code]) => String(code).toUpperCase() !== 'OMR')
                  .slice(0, 6)
                  .map(([code, rate]) => (
                    <span
                      key={code}
                      className="inline-flex items-center gap-1 rounded-full bg-neutral-50 border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-700"
                      data-testid={`outstanding-fx-chip-${code}`}
                    >
                      <span className="uppercase font-semibold tracking-wide">{String(code).toUpperCase()}</span>
                      <span className="text-neutral-400">·</span>
                      <span className="font-bold tabular-nums">{Number(rate).toFixed(4)}</span>
                    </span>
                  ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRatesOpen((v) => !v)}
                  data-testid="outstanding-fx-rates-toggle"
                >
                  {ratesOpen ? 'Close' : 'Edit'}
                </Button>
              </div>
            </div>
            {ratesOpen && (
              <div className="mt-3 pt-3 border-t border-neutral-100" data-testid="outstanding-fx-rates-editor">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {supportedCurrencies
                    .filter((c) => c.code !== 'OMR')
                    .map((c) => (
                      <label key={c.code} className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] uppercase font-semibold tracking-wide text-neutral-500 leading-none">{c.code}</div>
                          <div className="text-[10px] text-neutral-400 truncate">{c.name}</div>
                        </div>
                        <span className="text-[10px] text-neutral-500">=</span>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={ratesDraft[c.code] ?? exchangeRates[c.code] ?? ''}
                          placeholder="0.0000"
                          onChange={(e) => {
                            const v = e.target.value;
                            setRatesDraft((prev) => ({ ...(prev || {}), [c.code]: v === '' ? '' : Number(v) }));
                          }}
                          className="w-24 text-right tabular-nums text-[12px] font-semibold border border-neutral-300 rounded px-1 py-0.5"
                          data-testid={`outstanding-fx-input-${c.code}`}
                        />
                        <span className="text-[10px] font-semibold text-neutral-500">OMR</span>
                      </label>
                    ))}
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setRatesDraft({ ...(exchangeRates || {}) }); setRatesOpen(false); }}
                    disabled={savingRates}
                    data-testid="outstanding-fx-rates-cancel"
                  >Cancel</Button>
                  <Button
                    size="sm"
                    onClick={saveExchangeRates}
                    disabled={savingRates}
                    data-testid="outstanding-fx-rates-save"
                  >{savingRates ? 'Saving…' : 'Save rates'}</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-division compact tiles — hidden when the super-admin has
            toggled `show_division_tiles_admin` off. Sourced from
            server-side aggregation (`totals_by_category`), so figures
            reflect the ENTIRE filtered dataset and byte-match the main
            "Total Outstanding" KPI. */}
        {showDivTilesAdmin && byCategory.length > 0 && (
          <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3" data-testid="outstanding-by-category">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold text-neutral-500 uppercase tracking-wide">Outstanding by division</div>
              {isSuper && (
                <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Toggle division tiles on the admin dashboard">
                    <input
                      type="checkbox"
                      checked={showDivTilesAdmin}
                      disabled={savingDivToggle}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setShowDivTilesAdmin(next);
                        saveDivisionTilesToggle(next, showDivTilesPublic);
                      }}
                      className="w-3.5 h-3.5 accent-[#0078D4]"
                      data-testid="outstanding-toggle-div-tiles-admin"
                    />
                    <span>Show on admin</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Toggle division tiles on every public share link">
                    <input
                      type="checkbox"
                      checked={showDivTilesPublic}
                      disabled={savingDivToggle}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setShowDivTilesPublic(next);
                        saveDivisionTilesToggle(showDivTilesAdmin, next);
                      }}
                      className="w-3.5 h-3.5 accent-[#0078D4]"
                      data-testid="outstanding-toggle-div-tiles-public"
                    />
                    <span>Show on public links</span>
                  </label>
                </div>
              )}
            </div>
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
              {byCategory.map(({ slug, label, amount }) => {
                const sty = catStyle(slug);
                const cur = (divisionCurrencies && divisionCurrencies[String(slug || '').toLowerCase()]) || 'OMR';
                const curU = String(cur).toUpperCase();
                // `?v=2` cache-buster so freshly-deployed PNGs load
                // on the first render after rollout.
                const glyphSrc = curU === 'OMR' ? '/omr-rial.png?v=2' : (curU === 'AED' ? '/aed-symbol.png?v=3' : null);
                // Oct 2026 (user request) — The division tile now
                // shows its NATIVE-currency figure (AED/SAR/…) with
                // the matching flag + currency label, while the
                // Total Outstanding KPI card above keeps the
                // OMR-converted aggregate. Reverse-divide the OMR
                // sum sent by the server using the live
                // `exchangeRates` map (generalised across every
                // non-OMR code; `aedToOmrRate` is only consulted
                // when AED is missing from the table). OMR tiles
                // pass through unchanged.
                const tileRate = (curU === 'OMR')
                  ? 1
                  : (Number(exchangeRates?.[curU]) > 0
                      ? Number(exchangeRates[curU])
                      : (curU === 'AED' && Number(aedToOmrRate) > 0 ? Number(aedToOmrRate) : 1));
                const displayAmount = tileRate > 0 ? amount / tileRate : amount;
                const tileCur = curU;
                const tileGlyphSrc = glyphSrc;
                return (
                  <div
                    key={slug || '__unassigned__'}
                    className="rounded-lg px-2.5 py-2 flex items-center gap-2"
                    style={{ background: sty.bg, border: `1px solid ${sty.dot}22` }}
                    data-testid={`outstanding-cat-${slug || 'unassigned'}`}
                    data-tile-currency={tileCur}
                    data-tile-amount-display={displayAmount}
                  >
                    <span className="rounded-full flex-shrink-0" style={{ width: 8, height: 8, background: sty.dot }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-wide leading-none flex items-center gap-1" style={{ color: sty.fg }}>
                        <span className="truncate">{label}</span>
                        {tileGlyphSrc ? (
                          <img
                            src={tileGlyphSrc}
                            alt={tileCur}
                            aria-hidden="true"
                            className="ml-auto flex-shrink-0 pointer-events-none"
                            style={{ height: '14px', width: 'auto', transition: 'none', animation: 'none' }}
                            data-testid={`outstanding-cat-${slug || 'unassigned'}-currency-glyph`}
                          />
                        ) : (
                          <span
                            className="ml-auto tabular-nums font-bold"
                            style={{ fontSize: '9px', letterSpacing: '.02em', opacity: .8 }}
                            data-testid={`outstanding-cat-${slug || 'unassigned'}-currency`}
                          >{tileCur}</span>
                        )}
                      </div>
                      <div className="text-sm font-semibold tabular-nums mt-1 leading-none" style={{ color: sty.fg }}>{fmtMoney(displayAmount)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* When admin tiles are hidden but the viewer is super-admin, we
            still need a way to re-enable them. A discreet inline row
            keeps the setting always reachable. */}
        {!showDivTilesAdmin && isSuper && (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-3 py-2 flex items-center justify-between text-xs text-neutral-500" data-testid="outstanding-by-category-hidden">
            <span>Division tiles hidden.</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showDivTilesAdmin}
                  disabled={savingDivToggle}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setShowDivTilesAdmin(next);
                    saveDivisionTilesToggle(next, showDivTilesPublic);
                  }}
                  className="w-3.5 h-3.5 accent-[#0078D4]"
                  data-testid="outstanding-toggle-div-tiles-admin-restore"
                />
                <span>Show on admin</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showDivTilesPublic}
                  disabled={savingDivToggle}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setShowDivTilesPublic(next);
                    saveDivisionTilesToggle(showDivTilesAdmin, next);
                  }}
                  className="w-3.5 h-3.5 accent-[#0078D4]"
                  data-testid="outstanding-toggle-div-tiles-public-restore"
                />
                <span>Show on public links</span>
              </label>
            </div>
          </div>
        )}

        {/* KPIs — when a specific aging bucket is selected, the tile row
             collapses to a single "Total in {bucket}" figure so the
             report zooms in on that bucket. "All aging" restores the
             full 5-tile summary (Feb 2026 user request).

             Feb 2026 (division-awareness) — the Total Outstanding
             tile now spells out the active division / category
             filter in its label so a filtered figure never reads
             like a grand total. When both `division` and
             `category` (aka Division facet) are unfiltered, we
             show "- All Divisions"; when one is set, we append
             the selected value. If BOTH are set to different
             values, we join them with " · " for full
             transparency. */}
        {(() => {
          // Nothing else on the page needs this — a local IIFE
          // keeps the label helper close to the tile that uses it.
          const _div = division && division !== '__all__' ? division : '';
          const _cat = category && category !== '__all__' ? category : '';
          const parts = [_div, _cat].filter(Boolean);
          // De-duplicate — if the same value appears in both
          // filters we still only want one label suffix.
          const uniqueParts = Array.from(new Set(parts));
          const divisionSuffix = uniqueParts.length === 0
            ? 'All Divisions'
            : uniqueParts.join(' · ');
          const totalOsLabel = `Total Outstanding — ${divisionSuffix}`;
          const bucketLabel = agingBucket !== '__all__'
            ? `Total in ${AGING_LABEL[agingBucket] || agingBucket} — ${divisionSuffix}`
            : '';
          return agingBucket === '__all__' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4" data-testid="outstanding-kpis">
              <Kpi icon={Wallet}     label={totalOsLabel} value={fmtMoney(kpiNative(kpis.totalOs))}  color="blue"   testId="kpi-total-os" compact showRial currency={singleCurrency} />
              <MovementKpi movement={osMovement} loading={osMovementLoading} nativeDivisor={kpiNativeDivisor} nativeCurrency={singleCurrency} />
              <Kpi icon={ShieldOff}     label="Total Uncovered"    value={fmtMoney(kpiNative(kpis.uncov))}     color="melon"   testId="kpi-uncovered" showRial currency={singleCurrency} />
              <Kpi icon={AlertTriangle} label="Overdue >90 Days"  value={fmtMoney(kpiNative(kpis.overdue))}   color="red"    testId="kpi-overdue" shimmer showRial currency={singleCurrency} />
              <Kpi icon={TrendingUp}    label="PDC in hand"        value={fmtMoney(kpiNative(kpis.totalPdc))}  color="emerald" testId="kpi-pdc" showRial currency={singleCurrency} />
              <Kpi icon={Undo2}         label="Total Cheques Return" value={fmtMoney(kpiNative(kpis.dnAmt))}   color="amber" testId="kpi-dn-amt" showRial currency={singleCurrency} />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4" data-testid="outstanding-kpis-aging-only">
              <Kpi
                icon={AlertTriangle}
                label={bucketLabel}
                value={fmtMoney(kpiNative(kpis.agingBucketTotal))}
                color={['b91', 'b122', 'b150', 'b181', 'over181'].includes(agingBucket) ? 'red' : ['b30', 'b61'].includes(agingBucket) ? 'amber' : 'blue'}
                testId="kpi-aging-only"
                showRial
                currency={singleCurrency}
              />
            </div>
          );
        })()}

        {/* Filters */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px] w-full sm:w-auto">
              <Search className="w-4 h-4 absolute left-3 top-3 text-neutral-400" />
              <Input
                placeholder="Search by code or name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10"
                data-testid="outstanding-search"
              />
            </div>
            {/* Mobile: 2-col grid so 5 dropdowns take ≤3 rows instead of
                stacking to 5 rows. `sm:contents` unwraps the grid on ≥sm
                so filters flow into the parent flex-wrap exactly as
                before — desktop layout is unchanged. */}
            <div className="grid grid-cols-2 gap-3 w-full sm:contents" data-testid="outstanding-filter-grid">
              <CustomerMultiSelect
                options={customerDirectory.length > 0 ? customerDirectory : rows}
                value={customerCodes}
                onChange={setCustomerCodes}
                onKeywordChange={setCustomerKeyword}
                label="Customers"
                testId="outstanding-filter-customer"
                className="col-span-2 sm:col-span-1 sm:flex-1 sm:min-w-[300px]"
              />
              <MultiSelectFacet
                testId="outstanding-filter-salesman"
                placeholder="Salesman"
                options={facets.salesman || []}
                value={salesmen}
                onChange={setSalesmen}
              />
              <MultiSelectFacet
                testId="outstanding-filter-dept"
                placeholder="Dept"
                options={facets.dept || []}
                value={depts}
                onChange={setDepts}
              />
              <FacetSelect label="Division" value={division} onChange={setDivision} options={facets.division || []} testId="outstanding-filter-division" />
              <FacetSelect label="Type" value={custType} onChange={setCustType} options={facets.cust_type || []} testId="outstanding-filter-type" />
              <FacetSelect label="Credit" value={creditType} onChange={setCreditType} options={facets.credit_type || []} testId="outstanding-filter-credit" />
              <FacetSelect label="Division" value={category} onChange={setCategory} options={facets.category || []} testId="outstanding-filter-category" />
              <Select value={agingBucket} onValueChange={setAgingBucket}>
                <SelectTrigger className="h-10 w-full sm:w-[160px]" data-testid="outstanding-filter-aging"><SelectValue placeholder="Aging bucket" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All aging buckets</SelectItem>
                  <SelectItem value="curr">Only Current</SelectItem>
                  <SelectItem value="b30">Has ≤30</SelectItem>
                  <SelectItem value="b61">Has ≤61</SelectItem>
                  <SelectItem value="b91">Has ≤91</SelectItem>
                  <SelectItem value="b122">Has ≤122</SelectItem>
                  <SelectItem value="b150">Has ≤150</SelectItem>
                  <SelectItem value="b181">Has ≤181</SelectItem>
                  <SelectItem value="over181">Has &gt;181</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="relative">
              <Button variant="outline" onClick={() => setShowColMenu((s) => !s)} data-testid="outstanding-column-toggle-btn">
                <Columns3 className="w-4 h-4 mr-2" /> Columns
              </Button>
              {showColMenu && (
                <div className="absolute right-0 mt-2 z-30 w-64 bg-white rounded-2xl shadow-2xl border border-neutral-200 p-3" data-testid="outstanding-column-menu">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-neutral-500 uppercase">Visible columns</div>
                    <div className="flex gap-1">
                      <button onClick={() => setAllCols(true)} className="text-[11px] text-[#0078D4] hover:underline">All</button>
                      <span className="text-neutral-300">·</span>
                      <button onClick={() => setAllCols(false)} className="text-[11px] text-neutral-500 hover:underline">Min</button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto space-y-0.5">
                    {COLS.map((c) => (
                      <label key={c.key} className={`flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-neutral-50 ${c.alwaysOn ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={c.alwaysOn || visibleCols.includes(c.key)}
                          disabled={c.alwaysOn}
                          onChange={() => toggleCol(c.key)}
                          data-testid={`outstanding-col-toggle-${c.key}`}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button variant="ghost" onClick={resetFilters} data-testid="outstanding-clear-filters" className="text-neutral-500">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
          <div className="mt-2 text-xs text-neutral-500">
            Showing {sorted.length.toLocaleString()} of {(totalCount || rows.length).toLocaleString()} customers · Filtered total OS: <span className="font-semibold text-[#0F172A]" data-testid="outstanding-filtered-total">{(singleCurrency && kpiNativeDivisor !== 1) ? String(singleCurrency).toUpperCase() + ' ' : ''}{fmtMoney(kpiNative(filteredTotals.total_os))}</span>
          </div>
        </div>

        {/* Bulk-edit category toolbar — appears above the table, gets a
            blue outline while a selection is active so it's obvious that
            an action is armed. */}
        <div
          className={`rounded-2xl border p-3 flex flex-wrap items-center gap-3 transition ${
            selectedCodes.size > 0 ? 'border-[#0078D4] bg-[#DEECF9]/50 shadow-sm' : 'border-neutral-200 bg-white'
          }`}
          data-testid="outstanding-bulk-toolbar"
        >
          <button
            type="button"
            onClick={toggleSelectAllFiltered}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:text-[#0078D4] disabled:opacity-40"
            data-testid="outstanding-bulk-select-all"
            disabled={filtered.length === 0}
          >
            <input
              type="checkbox"
              readOnly
              checked={allFilteredSelected}
              ref={(el) => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
              className="w-4 h-4 cursor-pointer accent-[#0078D4]"
            />
            {allFilteredSelected ? 'Deselect all filtered' : 'Select all filtered'}
          </button>
          <div className="text-xs text-neutral-600" data-testid="outstanding-bulk-count">
            <b>{selectedCodes.size}</b> selected
            {filtered.length ? <span className="text-neutral-400"> · {filtered.length} in view</span> : null}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <span className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">Assign division</span>
            <select
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              disabled={bulkBusy || selectedCodes.size === 0}
              className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30 disabled:opacity-50"
              data-testid="outstanding-bulk-category-select"
            >
              <option value="">— Select division —</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <Button
              onClick={() => applyBulkCategory('assign')}
              disabled={bulkBusy || selectedCodes.size === 0 || !bulkCategory}
              className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white h-9"
              data-testid="outstanding-bulk-assign-btn"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Assign
            </Button>
            <Button
              variant="outline"
              onClick={() => applyBulkCategory('clear')}
              disabled={bulkBusy || selectedCodes.size === 0}
              className="rounded-full border-red-300 text-red-600 hover:bg-red-50 h-9"
              data-testid="outstanding-bulk-clear-btn"
              title="Clear the division on the selected customers"
            >
              Clear division
            </Button>
            {selectedCodes.size > 0 && (
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-neutral-500 hover:text-neutral-800 underline"
                data-testid="outstanding-bulk-deselect"
              >
                Deselect all
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-16 flex flex-col items-center gap-3 text-neutral-500">
              <Loader2 className="w-8 h-8 animate-spin text-[#0078D4]" /> Loading…
            </div>
          ) : !snapshot ? (
            <EmptyState onUpload={() => setUploadOpen(true)} />
          ) : (
            <div className="overflow-x-auto max-h-[70vh]" data-testid="outstanding-table">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th
                      style={{ minWidth: 40 }}
                      className="px-3 py-2.5 text-center font-semibold text-neutral-700 border-b border-neutral-200"
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={allFilteredSelected}
                        ref={(el) => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                        onClick={toggleSelectAllFiltered}
                        className="w-4 h-4 cursor-pointer accent-[#0078D4]"
                        aria-label="Select all filtered"
                        data-testid="outstanding-th-select"
                      />
                    </th>
                    {activeCols.map((c) => (
                      <th
                        key={c.key}
                        style={{ minWidth: c.w }}
                        className={`px-3 py-2.5 text-${c.align} font-semibold text-neutral-700 select-none cursor-pointer hover:text-[#0078D4] border-b border-neutral-200`}
                        onClick={() => onSort(c.key)}
                        data-testid={`outstanding-th-${c.key}`}
                      >
                        {c.label}
                        {sortKey === c.key && <span className="ml-1 text-[#0078D4]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(customerCodes.length > 0 ? sorted : sorted.slice(0, rowLimit)).map((r, idx) => {
                    const isSel = selectedCodes.has(r.customer_code);
                    // Multi-currency Stage 1 — resolve the row's currency
                    // from its category slug via the server-supplied map
                    // (falls back to OMR). Rendered as a subtle prefix
                    // on monetary cells for non-OMR rows so a mixed
                    // AED/OMR/SAR page reads unambiguously without
                    // adding an extra column.
                    const rowCur = (divisionCurrencies && divisionCurrencies[String(r.category || '').toLowerCase()]) || 'OMR';
                    const showCurPrefix = rowCur !== 'OMR';
                    // Feb 2026 double-AED fix. Non-marked rows (new
                    // currencies with no ingestion pass yet) are stored
                    // in their native currency, so no reverse-divide is
                    // needed. Marked rows (`_fx_converted_at_ingestion`
                    // or `aed_to_omr_backfill`) are stored as OMR already,
                    // so the native-currency figure the user wants on
                    // screen = stored_omr / applied_rate (e.g. 104.7 OMR
                    // / 0.1047 → 1,000 AED). Only the row body uses
                    // this; the Total / KPI tiles use the server-side
                    // OMR total and reverse-divide via `tileRate`.
                    let rowFxDivisor = 1;
                    if (r?._fx_converted_at_ingestion === true || r?.aed_to_omr_backfill === true) {
                      const appliedRate = Number(
                        r._fx_rate_applied ?? r.aed_to_omr_rate_used,
                      );
                      if (Number.isFinite(appliedRate) && appliedRate > 0) {
                        rowFxDivisor = appliedRate;
                      }
                    }
                    return (
                    <tr
                      key={`${r.customer_code}-${idx}`}
                      className={`border-b border-neutral-100 ${isSel ? 'bg-[#DEECF9]/50' : 'hover:bg-slate-50/60'}`}
                      data-testid={`outstanding-row-${r.customer_code}`}
                      data-row-currency={rowCur}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSelectCode(r.customer_code)}
                          className="w-4 h-4 cursor-pointer accent-[#0078D4]"
                          aria-label={`Select ${r.customer_code}`}
                          data-testid={`outstanding-row-select-${r.customer_code}`}
                        />
                      </td>
                      {activeCols.map((c) => {
                        const v = r[c.key];
                        const isNum = c.align === 'right';
                        const heat = c.heat && Number(v) > 0 ? HEAT_BG[c.heat] : '';
                        // Feb 2026: Cr Days / Cr Limit render as whole
                        // numbers (no baisa) since they aren't monetary
                        // aging figures.
                        const isInt = INT_FORMAT_KEYS.has(c.key);
                        const isMoney = isNum && !isInt;
                        // Stage 2 — reverse-convert money cells for
                        // AED rows so what the user sees matches the
                        // native currency. `credit_limit` is money
                        // (was multiplied at ingestion) → also
                        // reverse-converted here. `credit_days` is
                        // scalar → untouched.
                        const displayV = (isMoney && rowFxDivisor !== 1) ? (Number(v) || 0) / rowFxDivisor : v;
                        return (
                          <td
                            key={c.key}
                            style={{ minWidth: c.w }}
                            className={`px-3 py-2 text-${c.align} tabular-nums ${heat} ${c.bold ? 'font-semibold text-[#0F172A]' : ''}`}
                          >
                            {isNum ? (
                              isInt ? fmtInt(v) : (
                                showCurPrefix && Number(displayV) > 0 ? (
                                  <>
                                    {rowCur === 'AED' ? (
                                      <img
                                        src="/aed-symbol.png?v=3"
                                        alt="AED"
                                        aria-hidden="true"
                                        className="inline-block mr-1 select-none pointer-events-none align-[1px]"
                                        style={{ height: '11px', width: 'auto', transition: 'none', animation: 'none' }}
                                        data-testid={`outstanding-cell-${r.customer_code}-${c.key}-glyph`}
                                      />
                                    ) : (
                                      <span
                                        className="mr-1 text-[9px] font-bold uppercase tracking-wide text-neutral-500 align-[1px]"
                                        data-testid={`outstanding-cell-${r.customer_code}-${c.key}-currency`}
                                      >{rowCur}</span>
                                    )}
                                    {fmtMoney(displayV)}
                                  </>
                                ) : fmtMoney(displayV)
                              )
                            ) : (v ?? '')}
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={activeCols.length + 1} className="p-10 text-center text-neutral-500">
                        No customers match the current filters.
                      </td>
                    </tr>
                  )}
                  {sorted.length > rowLimit && customerCodes.length === 0 && (
                    <tr>
                      <td colSpan={activeCols.length + 1} className="p-4 text-center bg-slate-50 border-t-2 border-slate-200">
                        <span className="text-sm text-neutral-600 mr-3">
                          Showing first {rowLimit.toLocaleString()} of {sorted.length.toLocaleString()} rows for speed.
                        </span>
                        <button
                          onClick={() => setRowLimit(sorted.length)}
                          className="text-sm font-semibold text-[#0078D4] hover:underline"
                          data-testid="outstanding-show-all-btn"
                        >
                          Show all {sorted.length.toLocaleString()} rows
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
                {sorted.length > 0 && (
                  <tfoot className="sticky bottom-0 bg-white z-10 border-t-2 border-neutral-300">
                    <tr>
                      <td className="px-3 py-2 bg-slate-100" />
                      {activeCols.map((c, i) => {
                        // Feb 2026: Cr Days / Cr Limit are per-customer
                        // scalars — footer TOTAL is meaningless, so
                        // render an empty cell for these keys.
                        const showTotal = c.align === 'right' && !NON_SUMMABLE_KEYS.has(c.key);
                        return (
                          <td key={c.key} className={`px-3 py-2 text-${c.align} font-bold text-[#0F172A] bg-slate-100`}>
                            {i === 0
                              ? (singleCurrency && kpiNativeDivisor !== 1
                                  ? `TOTAL (${String(singleCurrency).toUpperCase()})`
                                  : 'TOTAL')
                              : (showTotal ? fmtMoney(kpiNative(filteredTotals[c.key])) : '')}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      {uploadOpen && (
        <OutstandingUploadModal
          onClose={() => setUploadOpen(false)}
          authHeaders={authHeaders}
          onUploaded={() => { loadLatest(); loadSnapshots(); }}
        />
      )}
      {historyOpen && (
        <SnapshotHistoryModal
          snapshots={snapshots}
          current={snapshot}
          onClose={() => setHistoryOpen(false)}
          onLoad={loadSnapshotById}
          onDelete={deleteSnapshot}
        />
      )}
      {linksOpen && (
        <ShareLinksModal
          onClose={() => setLinksOpen(false)}
          authHeaders={authHeaders}
          facets={facets}
        />
      )}
      {emailOpen && (
        <EmailReportModal
          onClose={() => setEmailOpen(false)}
          authHeaders={authHeaders}
          snapshot={snapshot}
          filters={{ q: search,
                     salesman: salesmen.length === 0 ? '' : (salesmen.length === 1 ? salesmen[0] : salesmen),
                     dept: depts.length === 0 ? '' : (depts.length === 1 ? depts[0] : depts),
                     division: division === '__all__' ? '' : division,
                     cust_type: custType === '__all__' ? '' : custType,
                     credit_type: creditType === '__all__' ? '' : creditType,
                     aging: agingBucket === '__all__' ? '' : agingBucket }}
          columns={visibleCols}
        />
      )}
      {subsOpen && (
        <SubscribersModal
          onClose={() => setSubsOpen(false)}
          authHeaders={authHeaders}
          facets={facets}
        />
      )}
      {salesmanRecipientsOpen && (
        <SalesmanRecipientsModal
          onClose={() => setSalesmanRecipientsOpen(false)}
          authHeaders={authHeaders}
        />
      )}
      {manualRecipientsOpen && (
        <ManualRecipientsModal
          onClose={() => setManualRecipientsOpen(false)}
          authHeaders={authHeaders}
        />
      )}
      {/* Super-admin confirm-before-cleanup dialog. The cleanup itself
          is idempotent + safeguarded server-side (403 for non-super),
          but the wording here makes the ONE-shot maintenance nature
          explicit so the button isn't punched by accident. */}
      <AlertDialog open={cleanupConfirmOpen} onOpenChange={setCleanupConfirmOpen}>
        <AlertDialogContent data-testid="outstanding-cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#B91C1C]" />
              Clean duplicate customer records?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This walks every outstanding snapshot and removes duplicate
              customer_code rows that were left behind by legacy per-division
              uploads. For each duplicate group, the row with a valid
              division value is kept (falling back to the larger total
              outstanding on ties). This is safe to run multiple times —
              a second call will simply report zero removals.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={cleanupRunning}
              data-testid="outstanding-cleanup-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={cleanupRunning}
              onClick={(e) => { e.preventDefault(); runCleanupDuplicates(); }}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
              data-testid="outstanding-cleanup-confirm-btn"
            >
              {cleanupRunning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cleaning…</>
              ) : (
                <>Run cleanup</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reconciliation audit modal — super-admin surface for tracking
          down the exact rows contributing to any OS Amt discrepancy
          between the source Excel and the app's KPI figure. Read-only. */}
      <AlertDialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <AlertDialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="outstanding-reconcile-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#0078D4]" />
              Outstanding reconciliation audit
            </AlertDialogTitle>
            <AlertDialogDescription>
              Compare the app&rsquo;s KPI total to the source Excel&rsquo;s
              <code className="mx-1 text-xs px-1 py-0.5 rounded bg-neutral-100">SUM(OS Amt)</code>
              cell. Any divergence is broken down by likely cause below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reconcileLoading && (
            <div className="flex items-center justify-center py-10 text-neutral-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Running audit…
            </div>
          )}
          {reconcileData && (
            <div className="space-y-4 text-sm" data-testid="outstanding-reconcile-body">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] uppercase font-bold text-neutral-500 tracking-wide">KPI total (shown on dashboard)</div>
                  <div className="text-lg font-extrabold tabular-nums text-[#0F172A] mt-1" data-testid="reconcile-kpi-total">{fmtMoney(reconcileData.agg_total)}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] uppercase font-bold text-neutral-500 tracking-wide">Raw row sum (all stored rows)</div>
                  <div className="text-lg font-extrabold tabular-nums text-[#0F172A] mt-1" data-testid="reconcile-raw-sum">{fmtMoney(reconcileData.raw_row_sum)}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] uppercase font-bold text-neutral-500 tracking-wide">Rows / distinct customers</div>
                  <div className="text-lg font-extrabold tabular-nums text-[#0F172A] mt-1">
                    {Number(reconcileData.row_count || 0).toLocaleString()} / {Number(reconcileData.distinct_customer_count || 0).toLocaleString()}
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] uppercase font-bold text-neutral-500 tracking-wide">Carried forward (rows / os)</div>
                  <div className="text-lg font-extrabold tabular-nums text-[#0F172A] mt-1" data-testid="reconcile-carry">
                    {Number(reconcileData.carried_forward_rows || 0).toLocaleString()} / {fmtMoney(reconcileData.carried_forward_os_sum)}
                  </div>
                </div>
              </div>

              {reconcileData.negative_os_row_count > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="reconcile-negatives">
                  <div className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-2">
                    Credit-balance rows detected ({reconcileData.negative_os_row_count} · total {fmtMoney(reconcileData.negative_os_sum)})
                  </div>
                  <div className="text-[11px] text-amber-800 mb-2">
                    These rows have a negative OS Amt (overpayments / credit balances).
                    Excel sums them correctly; the app now does too after the Feb 2026 parser fix
                    (accounting parentheses + CR/DR suffixes).
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {reconcileData.top_negative_os.slice(0, 10).map((r, idx) => (
                      <div key={idx} className="flex justify-between font-mono text-xs">
                        <span className="truncate mr-2">{r.customer_code} · {r.name}</span>
                        <span className="tabular-nums text-amber-900 font-bold">{fmtMoney(r.os_amt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reconcileData.case_variant_dupes && reconcileData.case_variant_dupes.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="reconcile-case-dupes">
                  <div className="text-xs font-bold text-red-900 uppercase tracking-wide mb-2">
                    Case-variant duplicate codes ({reconcileData.case_variant_dupes.length})
                  </div>
                  <div className="text-[11px] text-red-800 mb-2">
                    These customer codes match ignoring case — they&rsquo;re being counted separately in the KPI.
                    Fix the ERP export or use &ldquo;Clean duplicate records&rdquo;.
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {reconcileData.case_variant_dupes.slice(0, 10).map((d, idx) => (
                      <div key={idx} className="font-mono text-xs text-red-900">
                        {d.normalised}: {d.variants.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reconcileData.whitespace_variant_dupes && reconcileData.whitespace_variant_dupes.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="reconcile-ws-dupes">
                  <div className="text-xs font-bold text-red-900 uppercase tracking-wide mb-2">
                    Whitespace-variant duplicate codes ({reconcileData.whitespace_variant_dupes.length})
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {reconcileData.whitespace_variant_dupes.slice(0, 10).map((d, idx) => (
                      <div key={idx} className="font-mono text-xs text-red-900">
                        {d.normalised}: {d.variants.map((v) => JSON.stringify(v)).join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reconcileData.multi_row_customer_count > 0 && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="reconcile-multi-row">
                  <div className="text-xs font-bold text-blue-900 uppercase tracking-wide mb-2">
                    Customers with multiple rows ({reconcileData.multi_row_customer_count})
                  </div>
                  <div className="text-[11px] text-blue-800 mb-2">
                    If the KPI ever summed all rows (instead of deduping to one per customer),
                    the total would be {fmtMoney(reconcileData.multi_row_overcount_if_summed)} higher.
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {reconcileData.multi_row_customers.slice(0, 10).map((r, idx) => (
                      <div key={idx} className="flex justify-between font-mono text-xs">
                        <span className="truncate mr-2">{r.customer_code} · {r.row_count} rows · [{(r.categories || []).join(', ')}]</span>
                        <span className="tabular-nums text-blue-900 font-bold">Σ {fmtMoney(r.sum_os)} · max {fmtMoney(r.max_os)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-neutral-200 bg-white p-3">
                <div className="text-[10px] uppercase font-bold text-neutral-500 tracking-wide mb-1">Reconciliation hint</div>
                <div className="text-xs text-neutral-700">{reconcileData.app_vs_source_hint?.note}</div>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="outstanding-reconcile-close-btn">Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

const Kpi = ({ icon: Icon, label, value, color, testId, shimmer, compact, showRial, currency }) => {
  const palette = {
    blue:    'bg-[#DEECF9]/50 text-[#0078D4]',
    red:     'bg-red-50 text-red-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber:   'bg-amber-50 text-amber-600',
    // Melon/salmon accent (Feb 2026) — matches the public share view
    // `#F97066` for the Total Uncovered tile.
    melon:   'bg-[#FFF1F0] text-[#F97066]',
  }[color];
  // Figure (value) colour by tile. Alert-flavoured tiles carry their
  // accent through the number itself so a glance at the KPI row
  // instantly reads "red = overdue", "melon = uncovered". PDC (emerald)
  // and Total Cheques Return (amber) figures use a muted 75% gray to
  // de-emphasise them relative to the overdue alerts (Feb 2026 user
  // request). Neutral tiles keep rich-black for max readability.
  const valueColorClass = {
    red:     'text-red-600',
    melon:   'text-[#F97066]',
    emerald: 'text-neutral-500',
    amber:   'text-neutral-500',
  }[color] || 'text-[#0F172A]';
  // 2026-02 — User-specified exact shine spec. The value is wrapped in
  // a `.overdue-figure` span whose CSS (see App.css) paints a red
  // linear-gradient with a white band and animates a left→right shine.
  // No other animation, transition, keyframe or effect on this tile.
  const valueClassName = `text-base sm:text-xl md:text-2xl font-semibold tabular-nums truncate leading-tight ${valueColorClass}`;
  const labelClassName = `text-[10px] sm:text-xs uppercase tracking-wide font-semibold truncate text-neutral-500`;
  // 2026-08 — desktop tile height reduced across the board (`sm:p-3`
  // was `sm:p-4`) for a tighter, more balanced KPI row. `compact`
  // additionally shortens mobile padding for the two headline tiles
  // (Total Outstanding + OS Movement) so the mobile stack reads
  // proportionally next to the other tiles below them.
  const paddingClass = compact ? 'p-2 sm:p-3' : 'p-3 sm:p-3';
  return (
    <div className={`rounded-2xl border border-neutral-200 bg-white ${paddingClass} flex items-center gap-2 sm:gap-3 min-w-0`} data-testid={testId}>
      <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${palette}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={valueClassName}>
          {/* Feb 2026 — five-tile Rial mark. Sits at the top-left of
              the number value, small (8px mobile / 10px desktop), and
              deliberately static — no CSS transition, animation or
              hover effect (App.css :where(*) transitions are already
              scoped so this img is unaffected). Height is fixed in px
              so the tile's own height doesn't grow.
              Multi-currency Stage 1: `currency` (falls back to OMR)
              drives the mark — OMR keeps the historical rial glyph,
              AED renders the CBUAE Dirham glyph at the SAME height,
              any other currency falls back to a compact 3-letter code
              badge in the same slot. */}
          {showRial && (() => {
            const cur = String(currency || 'OMR').toUpperCase();
            const glyphSrc = cur === 'OMR' ? '/omr-rial.png?v=2' : (cur === 'AED' ? '/aed-symbol.png?v=3' : null);
            if (glyphSrc) {
              return (
                <img
                  src={glyphSrc}
                  alt={cur}
                  aria-hidden="true"
                  className="block sm:inline-block sm:align-top mr-1 select-none pointer-events-none"
                  style={{
                    height: '9px',
                    width: 'auto',
                    // On mobile the mark sits on its own line ABOVE the
                    // number (block display) so long amounts never
                    // overflow the compact tile. On ≥sm the mark reverts
                    // to inline placement with the 3px top offset that
                    // keeps it visually pinned to the top-left of the
                    // digits.
                    marginTop: '3px',
                    marginBottom: '2px',
                    transition: 'none',
                    animation: 'none',
                  }}
                  data-testid={`${testId}-${cur === 'OMR' ? 'rial' : 'currency-glyph'}`}
                />
              );
            }
            return (
              <span
                aria-label={cur}
                className="inline-block align-top mr-1 select-none pointer-events-none tabular-nums font-semibold"
                style={{
                  fontSize: '10px',
                  lineHeight: 1,
                  color: '#0F172A',
                  background: '#F1F5F9',
                  border: '1px solid #E2E8F0',
                  borderRadius: '4px',
                  padding: '2px 5px',
                  marginTop: '3px',
                  marginBottom: '2px',
                  letterSpacing: '.02em',
                }}
                data-testid={`${testId}-currency`}
              >{cur}</span>
            );
          })()}
          {shimmer
            ? <span className="overdue-figure" data-testid={`${testId}-shimmer`}>{value}</span>
            : value}
        </div>
        <div className={labelClassName}>{label}</div>
      </div>
    </div>
  );
};

// OS Movement tile (2026-08 redesign) — deep-navy background with an
// inline gradient area chart of the last 5-7 snapshot totals so the
// tile visually pops next to the plain KPI tiles. The bold figure
// + arrow live at the top, the mini-trend curve fills the mid
// section, and "OS Movement" anchors the bottom. Green when the
// latest movement is a decrease (OS shrank — money came in),
// red when it's an increase (OS grew — collections slipped),
// neutral slate when there's no previous snapshot yet.
const MovementKpi = ({ movement, loading, nativeDivisor = 1, nativeCurrency = null }) => {
  const hasPrev = !!(movement && movement.has_previous);
  // Oct 2026 — Reverse-divide the OMR delta into the single
  // non-OMR currency when the filter narrows scope to one. The
  // sparkline uses the same divisor so the trend line keeps its
  // shape in native-currency units.
  const divisor = (nativeDivisor && nativeDivisor !== 1) ? nativeDivisor : 1;
  const delta = hasPrev ? Number(movement.movement || 0) / divisor : 0;
  const isDown = hasPrev && delta < 0;
  const isUp   = hasPrev && delta > 0;
  const history = Array.isArray(movement?.history) ? movement.history : [];
  // Restrict to a max of 7 points; if we somehow got only one we
  // synthesise a flat 2-point series so the chart can still draw
  // (single point looks broken as a line).
  const pts = history.slice(-7).map((h) => Number(h?.os_amt || 0) / divisor);
  const drawablePts = pts.length >= 2 ? pts : (pts.length === 1 ? [pts[0], pts[0]] : []);

  // Palette — pick BEFORE building the SVG so the same tokens flow
  // to both stroke and gradient stops.
  const palette = isDown
    ? { stroke: '#10B981', glow: '#10B981', text: '#6EE7B7', arrow: '#10B981', accent: 'shadow-[0_0_24px_rgba(16,185,129,0.35)]' }
    : (isUp
      ? { stroke: '#EF4444', glow: '#EF4444', text: '#FCA5A5', arrow: '#EF4444', accent: 'shadow-[0_0_24px_rgba(239,68,68,0.35)]' }
      : { stroke: '#94A3B8', glow: '#64748B', text: '#CBD5E1', arrow: '#94A3B8', accent: '' });

  // Build the SVG path — smooth Catmull-Rom → cubic bezier so the
  // curve reads as an organic trend line rather than a jagged
  // polyline. Chart is normalised to a 100×36 viewBox so it
  // scales cleanly inside the tile.
  const W = 100;
  const H = 36;
  const pad = 2;
  let linePath = '';
  let areaPath = '';
  if (drawablePts.length >= 2) {
    const min = Math.min(...drawablePts);
    const max = Math.max(...drawablePts);
    const rng = max - min || 1;
    const xs = drawablePts.map((_, i) => pad + (i * (W - 2 * pad)) / (drawablePts.length - 1));
    const ys = drawablePts.map((v) => H - pad - ((v - min) / rng) * (H - 2 * pad));
    const points = xs.map((x, i) => [x, ys[i]]);
    // Smooth cubic bezier via Catmull-Rom approximation.
    const seg = (p0, p1, p2, p3) => {
      const c1x = p1[0] + (p2[0] - p0[0]) / 6;
      const c1y = p1[1] + (p2[1] - p0[1]) / 6;
      const c2x = p2[0] - (p3[0] - p1[0]) / 6;
      const c2y = p2[1] - (p3[1] - p1[1]) / 6;
      return `C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
    };
    linePath = `M${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      linePath += ' ' + seg(p0, p1, p2, p3);
    }
    areaPath = `${linePath} L${points[points.length - 1][0].toFixed(2)},${H} L${points[0][0].toFixed(2)},${H} Z`;
  }

  const arrow = isDown ? '↓' : (isUp ? '↑' : '–');
  const sign = isUp ? '+' : (isDown ? '−' : '');
  // Oct 2026 — Currency prefix follows the single-non-OMR scope so
  // the Movement tile matches the Total Outstanding KPI next to it.
  // Falls back to "OMR" for mixed/unfiltered views (divisor === 1).
  const _mvCurCode = (nativeCurrency && String(nativeCurrency).toUpperCase() !== 'OMR' && divisor !== 1)
    ? String(nativeCurrency).toUpperCase()
    : 'OMR';
  const _mvCurPrefix = `${_mvCurCode} `;
  const displayValue = !hasPrev
    ? '—'
    : (loading ? '…' : `${_mvCurPrefix}${sign}${fmtMoney(Math.abs(delta))}`);
  const gradId = `os-mv-grad-${isDown ? 'd' : isUp ? 'u' : 'f'}`;
  const tooltip = hasPrev
    ? `vs previous snapshot (${formatDateTime(movement.previous_snapshot_at)})`
    : 'No previous snapshot to compare against yet';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-[#0B1220] text-white p-2 sm:p-3 flex flex-col justify-between min-w-0 border border-white/5 ${palette.accent}`}
      title={tooltip}
      data-testid="kpi-os-movement"
    >
      {/* Subtle radial highlight so the dark tile doesn't feel flat next
          to the plain white KPI tiles. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background: `radial-gradient(circle at 20% 0%, ${palette.glow}22 0%, transparent 55%)`,
        }}
      />
      <div className="relative flex items-center justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div
            className="text-lg sm:text-2xl md:text-3xl font-bold tabular-nums leading-tight truncate"
            style={{ color: palette.text }}
            data-testid="kpi-os-movement-value"
          >
            {displayValue}
          </div>
        </div>
        <div
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg sm:text-xl font-black"
          style={{
            background: `${palette.arrow}22`,
            color: palette.arrow,
            border: `1px solid ${palette.arrow}55`,
          }}
        >
          {arrow}
        </div>
      </div>

      {/* Mini gradient area chart — the visual heart of the tile. */}
      <div className="relative mt-1 -mx-1">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-5 sm:h-10">
          <defs>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={palette.glow} stopOpacity="0.55" />
              <stop offset="100%" stopColor={palette.glow} stopOpacity="0" />
            </linearGradient>
          </defs>
          {areaPath && (
            <path d={areaPath} fill={`url(#${gradId})`} data-testid="kpi-os-movement-area" />
          )}
          {linePath && (
            <path d={linePath} fill="none" stroke={palette.stroke} strokeWidth="1.75"
                  strokeLinecap="round" strokeLinejoin="round"
                  data-testid="kpi-os-movement-line" />
          )}
        </svg>
      </div>

      <div className="relative mt-1 flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold text-white/60">
            OS Movement
          </div>
          <div className="text-[9px] sm:text-[10px] text-white/40 leading-tight truncate" data-testid="kpi-os-movement-subtitle">
            vs previous update
          </div>
        </div>
        <div className="text-[9px] sm:text-[10px] text-white/40 tabular-nums flex-shrink-0">
          {history.length > 1 ? `last ${history.length}` : ''}
        </div>
      </div>
    </div>
  );
};

const FacetSelect = ({ label, value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full sm:w-[160px]" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="__all__">All {label.toLowerCase()}s</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const EmptyState = ({ onUpload }) => (
  <div className="p-16 text-center" data-testid="outstanding-empty">
    <FileSpreadsheet className="w-14 h-14 mx-auto text-neutral-300 mb-3" />
    <h3 className="text-lg font-bold text-[#0F172A]">No outstanding snapshot yet</h3>
    <p className="text-sm text-neutral-500 mt-1 mb-4">
      Upload your ERP outstanding Excel export to see aging analysis.
    </p>
    <Button onClick={onUpload} className="bg-[#0078D4] hover:bg-[#005A9E] text-white">
      <Upload className="w-4 h-4 mr-2" /> Upload snapshot
    </Button>
  </div>
);

const OutstandingUploadModal = ({ onClose, authHeaders, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [merge, setMerge] = useState(true);
  const { toast } = useToast();

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/outstanding/template`, { headers: authHeaders, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'outstanding-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast({ title: 'Template download failed', description: e.response?.data?.detail || 'Try again' });
    } finally { setDownloading(false); }
  };

  // Loud four-tone alert chime — reused after a successful outstanding
  // upload to catch admins who kicked off a long consolidated import and
  // walked away from the screen (Feb 2026 user request: the previous
  // silent toast was too easy to miss). Volume envelope mirrors the
  // driver-alert profile in NotificationsBell so it's noticeably louder
  // than the standard 2-tone admin chime — sine + square overdrive
  // through a 0.9 masterGain. Works on desktop and mobile: since this
  // fires DIRECTLY after a user-initiated upload button click, iOS /
  // Android autoplay gates are already unlocked by the gesture.
  const playLoudAlertChime = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      if (ctx.state === 'suspended') { try { ctx.resume(); } catch { /* ignore */ } }
      const now = ctx.currentTime;
      const pattern = [
        { freq: 1046, dur: 0.18 }, // C6
        { freq: 1568, dur: 0.18 }, // G6
        { freq: 1046, dur: 0.18 }, // C6
        { freq: 1975, dur: 0.36 }, // B6 — sustained tail
      ];
      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.9;
      masterGain.connect(ctx.destination);
      let offset = 0;
      pattern.forEach(({ freq, dur }) => {
        [
          { type: 'sine',   g: 0.55 },
          { type: 'square', g: 0.20 },
        ].forEach(({ type, g }) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = type;
          osc.frequency.setValueAtTime(freq, now + offset);
          gain.gain.setValueAtTime(0, now + offset);
          gain.gain.linearRampToValueAtTime(g, now + offset + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, now + offset + dur);
          osc.connect(gain).connect(masterGain);
          osc.start(now + offset);
          osc.stop(now + offset + dur + 0.05);
        });
        offset += dur + 0.05;
      });
      // Release the AudioContext after the tail dies out so mobile
      // browsers don't count it as "sticky playing" audio.
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1600);
    } catch { /* autoplay policy / no WebAudio — silent no-op */ }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // merge=true → overlay this file's customer_codes onto the previous
      // snapshot (categories not present here are carried forward).
      // merge=false → legacy "replace everything" behaviour.
      fd.append('merge', merge ? 'true' : 'false');
      const res = await axios.post(`${API}/admin/outstanding/upload`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      const cf = Number(res.data.carried_forward_rows || 0);
      const newr = Number(res.data.new_rows || res.data.row_count || 0);
      toast({
        title: 'Upload complete',
        description: merge
          ? `${newr} new/updated · ${cf} carried forward from previous snapshot`
          : `${res.data.row_count} rows imported (replace mode)`,
      });
      // Loud audible confirmation — see `playLoudAlertChime` docstring.
      playLoudAlertChime();
      if (onUploaded) onUploaded(res.data);
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-upload-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#DEECF9] flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-[#0078D4]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0F172A]">Upload outstanding snapshot</h3>
              <p className="text-xs text-neutral-500">Excel (.xlsx) or CSV — creates a new dated snapshot.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="outstanding-upload-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!result && (
            <>
              <div className="rounded-2xl border border-dashed border-[#0078D4]/40 bg-[#DEECF9]/30 p-5">
                <div className="text-sm font-semibold text-[#0F172A] mb-1">Step 1 — Download template</div>
                <p className="text-xs text-neutral-600 mb-3">Or upload the raw ERP export as-is.</p>
                <Button onClick={downloadTemplate} disabled={downloading} className="h-10 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold" data-testid="outstanding-template-btn">
                  <Download className="w-4 h-4 mr-1.5" /> {downloading ? 'Preparing…' : 'Download template'}
                </Button>
              </div>

              <div className="rounded-2xl border border-neutral-200 p-5">
                <div className="text-sm font-semibold text-[#0F172A] mb-2">Step 2 — Choose upload mode</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <label
                    className={`flex items-start gap-3 rounded-2xl border-2 p-3 cursor-pointer transition ${merge ? 'border-[#0078D4] bg-[#DEECF9]/40' : 'border-neutral-200 hover:border-neutral-300'}`}
                    data-testid="outstanding-mode-merge"
                  >
                    <input
                      type="radio"
                      name="upload-mode"
                      checked={merge}
                      onChange={() => setMerge(true)}
                      className="mt-1 accent-[#0078D4]"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-[#0F172A]">Merge with previous snapshot</div>
                      <div className="text-xs text-neutral-600 mt-0.5">
                        Overlay this file's customers on top of the previous data. Customers not in this file (other categories) are <b>carried forward</b> unchanged.
                      </div>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 rounded-2xl border-2 p-3 cursor-pointer transition ${!merge ? 'border-red-400 bg-red-50/60' : 'border-neutral-200 hover:border-neutral-300'}`}
                    data-testid="outstanding-mode-replace"
                  >
                    <input
                      type="radio"
                      name="upload-mode"
                      checked={!merge}
                      onChange={() => setMerge(false)}
                      className="mt-1 accent-red-500"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-[#0F172A]">Replace everything</div>
                      <div className="text-xs text-neutral-600 mt-0.5">
                        The new snapshot contains <b>only</b> the customers in this file. Use for a full company-wide export.
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-sm font-semibold text-[#0F172A] mb-2">Step 3 — Upload your file</div>
                <label className={`flex flex-col items-center justify-center w-full h-40 rounded-2xl border-2 border-dashed cursor-pointer transition ${uploading ? 'bg-slate-50 border-slate-300' : 'bg-slate-50 hover:bg-[#DEECF9]/30 border-slate-300 hover:border-[#0078D4]'}`} data-testid="outstanding-upload-dropzone">
                  <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} data-testid="outstanding-upload-input" />
                  {uploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-[#0078D4] animate-spin mb-2" />
                      <span className="text-sm font-medium text-[#0078D4]">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                      <span className="text-sm font-medium text-neutral-700">Click to choose .xlsx or .csv</span>
                      <span className="text-xs text-neutral-500 mt-1">max 10 MB · {merge ? 'merges into current snapshot' : 'replaces current snapshot'}</span>
                    </>
                  )}
                </label>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-4" data-testid="outstanding-upload-result">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                <div className="text-sm font-semibold text-emerald-700 mb-1">Import complete</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  <div><div className="text-2xl font-extrabold text-emerald-700">{result.new_rows ?? result.row_count}</div><div className="text-xs text-emerald-600">New / updated</div></div>
                  <div><div className="text-2xl font-extrabold text-[#0078D4]">{result.carried_forward_rows ?? 0}</div><div className="text-xs text-[#0078D4]">Carried forward</div></div>
                  <div><div className="text-2xl font-extrabold text-red-700">{result.failed}</div><div className="text-xs text-red-600">Rows failed</div></div>
                  <div><div className="text-2xl font-extrabold text-[#0F172A]">{fmtMoney(result.total_os_amt)}</div><div className="text-xs text-neutral-500">Total OS</div></div>
                </div>
                {result.merge_mode === false && (
                  <div className="text-xs text-red-700 mt-3">Replace mode was used — customers not in this file have been dropped from the current snapshot.</div>
                )}
              </div>
              {result.errors?.length > 0 && (
                <div className="rounded-2xl border border-red-200 bg-red-50/50 p-4">
                  <div className="text-sm font-semibold text-red-700 mb-2">Errors</div>
                  <ul className="text-xs text-red-700 space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i} className="font-mono">Row {e.row} — {e.reason}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setResult(null)} className="rounded-full">Upload another</Button>
                <Button onClick={onClose} className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white" data-testid="outstanding-upload-done">Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SnapshotHistoryModal = ({ snapshots, current, onClose, onLoad, onDelete }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-history-modal">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
        <h3 className="text-lg font-bold text-[#0F172A]">Snapshot history</h3>
        <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 space-y-2">
        {snapshots.length === 0 && <p className="text-sm text-neutral-500 text-center py-8">No snapshots uploaded yet.</p>}
        {snapshots.map((s) => (
          <div key={s.id} className={`rounded-2xl border p-3 flex items-center gap-3 ${current?.id === s.id ? 'border-[#0078D4] bg-[#DEECF9]/30' : 'border-neutral-200'}`}>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0F172A]">{fmtDate(s.snapshot_at)}</div>
              <div className="text-xs text-neutral-500">by {s.uploaded_by} · {s.row_count} rows · Total OS {fmtMoney(s.total_os_amt)}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onLoad(s.id)} data-testid={`outstanding-load-${s.id}`}><Eye className="w-4 h-4 mr-1" /> View</Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)} className="text-red-600 hover:text-red-700" data-testid={`outstanding-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ShareLinksModal = ({ onClose, authHeaders, facets }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  // Feb 2026 — baked salesman/division are now ARRAYS to support
  // multi-select ("show HASSAN + AHMED's combined book on one link").
  // Empty array === "no lock" and mints an unbaked link (same as before).
  // Legacy single-value payloads produced by earlier UI code paths are
  // still accepted by the backend, so downgrade rollout is safe.
  const [bakedSalesman, setBakedSalesman] = useState([]);
  const [bakedDivision, setBakedDivision] = useState([]);
  // 2026-07-21: dept lock in share-link creation. Multi-select so the
  // admin can pin the link to one OR multiple departments (mirrors the
  // salesman / division baked-facet UX).
  const [bakedDept, setBakedDept] = useState([]);
  // Global ticker speed for every share link (seconds per full scroll).
  // Persisted in `settings.outstanding.ticker_speed_seconds` and applied
  // at HTML render time — no need to remint links after tweaking.
  const [tickerSpeed, setTickerSpeed] = useState(90);
  const [savingSpeed, setSavingSpeed] = useState(false);
  // Guard flag flipped on while the user is actively dragging the range
  // slider (mouse down / touch start) and cleared on release. Prevents
  // the initial server hydrate — or any future refetch — from snapping
  // the thumb back to the persisted value mid-drag. `useRef` (not
  // state) so mutations during drag don't trigger extra renders and
  // fight the browser's own drag handling.
  const isAdjustingSpeedRef = useRef(false);
  // Column visibility per share-link — mirrors the Live Stock Report's
  // per-link column hide/unhide. `null` (all-checked) → the backend
  // stores columns=null which means "no restriction". Otherwise we send
  // an explicit list of column keys the audience is allowed to see.
  const HIDEABLE_COLS = COLS.filter((c) => !c.alwaysOn);
  const [colVis, setColVis] = useState(() => Object.fromEntries(HIDEABLE_COLS.map((c) => [c.key, true])));
  const [editingCols, setEditingCols] = useState(null);  // slug currently in the "edit columns" state
  const [editColVis, setEditColVis] = useState({});
  // Filter-widget hide/unhide (7 filters: search box + 5 facet dropdowns + aging bucket).
  const FILTER_KEYS = [
    { key: 'search',      label: 'Search box' },
    { key: 'salesman',    label: 'All salesmen' },
    { key: 'division',    label: 'All categories' },
    { key: 'cust_type',   label: 'All types' },
    { key: 'credit_type', label: 'All credit' },
    { key: 'aging',       label: 'All aging' },
  ];
  const [editingFilters, setEditingFilters] = useState(null); // slug currently in the "edit filters" state
  const [editFilterHidden, setEditFilterHidden] = useState(new Set());
  // Feb 2026 — inline "Add divisions" editor per existing link. Lets
  // the admin append more divisions onto a link WITHOUT regenerating
  // the URL. The current divisions are shown as read-only chips so
  // it's obvious what's already baked, and the multi-select below
  // only lists the divisions NOT yet attached.
  const [editingDivisions, setEditingDivisions] = useState(null); // slug
  const [editDivAdd, setEditDivAdd] = useState([]);               // array of slugs to append
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/outstanding/share-links`, { headers: authHeaders });
      setLinks(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Hydrate ticker-speed from server on open + provide a save handler.
  // The `isAdjustingSpeedRef` guard makes this idempotent-safe: if the
  // response ever arrives while the admin is already dragging (slow
  // network, tab was backgrounded, etc), we skip the state overwrite so
  // the thumb doesn't jump.
  //
  // Also caches the division-tiles toggles so a ticker-speed save
  // doesn't wipe them (the PUT $sets the whole `outstanding` subdoc).
  const showDivAdminRef = useRef(true);
  const showDivPublicRef = useRef(true);
  const maintenanceModeRef = useRef(false);
  useEffect(() => {
    axios.get(`${API}/admin/outstanding/config`, { headers: authHeaders })
      .then(({ data }) => {
        if (isAdjustingSpeedRef.current) return;
        setTickerSpeed(Number(data?.ticker_speed_seconds || 90));
        if (data?.show_division_tiles_admin !== undefined) showDivAdminRef.current = !!data.show_division_tiles_admin;
        if (data?.show_division_tiles_public !== undefined) showDivPublicRef.current = !!data.show_division_tiles_public;
        if (data?.maintenance_mode !== undefined) maintenanceModeRef.current = !!data.maintenance_mode;
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTickerSpeed = async (nextRaw) => {
    const next = Math.max(10, Math.min(600, parseInt(nextRaw, 10) || 90));
    setTickerSpeed(next);
    setSavingSpeed(true);
    try {
      await axios.put(`${API}/admin/outstanding/config`,
        {
          ticker_speed_seconds: next,
          show_division_tiles_admin: showDivAdminRef.current,
          show_division_tiles_public: showDivPublicRef.current,
          maintenance_mode: maintenanceModeRef.current,
        },
        { headers: authHeaders });
      toast({ title: 'Ticker speed saved', description: `${next} s per full scroll · applied to every link` });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSavingSpeed(false); }
  };

  const mint = async () => {
    setCreating(true);
    try {
      const facetsPayload = {};
      if (bakedSalesman.length > 0) {
        // Send a scalar when only one is picked so the DB payload stays
        // shape-compatible with pre-Feb-2026 links stored as strings.
        // Multi-selects go over the wire as arrays — the backend
        // `_build_snapshot_query` / `_apply_filters` handle both shapes.
        facetsPayload.salesman = bakedSalesman.length === 1 ? bakedSalesman[0] : bakedSalesman;
      }
      if (bakedDivision.length > 0) {
        facetsPayload.division = bakedDivision.length === 1 ? bakedDivision[0] : bakedDivision;
      }
      // 2026-07-21: dept lock — same scalar/array shape convention as
      // the salesman / division baked facets above.
      if (bakedDept.length > 0) {
        facetsPayload.dept = bakedDept.length === 1 ? bakedDept[0] : bakedDept;
      }
      // Only send the columns list when the admin has hidden at least
      // one — otherwise pass null so the audience sees everything.
      const hiddenAny = HIDEABLE_COLS.some((c) => !colVis[c.key]);
      const payload = { label, facets: facetsPayload };
      if (hiddenAny) {
        payload.columns = ['customer_code', 'name', ...HIDEABLE_COLS.filter((c) => colVis[c.key]).map((c) => c.key)];
      }
      const r = await axios.post(`${API}/admin/outstanding/share-links`, payload, { headers: authHeaders });
      setLinks((p) => [r.data, ...p]);
      setLabel(''); setBakedSalesman([]); setBakedDivision([]); setBakedDept([]);
      setColVis(Object.fromEntries(HIDEABLE_COLS.map((c) => [c.key, true])));
      toast({ title: 'Share link created' });
    } catch (e) { toast({ title: 'Create failed', description: e.response?.data?.detail || 'Try again.' }); }
    finally { setCreating(false); }
  };

  const startEditCols = (link) => {
    const cur = new Set(link.columns || COLS.map((c) => c.key));
    setEditColVis(Object.fromEntries(HIDEABLE_COLS.map((c) => [c.key, cur.has(c.key)])));
    setEditingCols(link.slug);
  };

  const saveEditCols = async (slug) => {
    try {
      const hiddenAny = HIDEABLE_COLS.some((c) => !editColVis[c.key]);
      const cols = hiddenAny
        ? ['customer_code', 'name', ...HIDEABLE_COLS.filter((c) => editColVis[c.key]).map((c) => c.key)]
        : null;
      await axios.patch(`${API}/admin/outstanding/share-links/${slug}`, { columns: cols }, { headers: authHeaders });
      setEditingCols(null);
      await load();
      toast({ title: 'Columns updated' });
    } catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' }); }
  };

  const startEditFilters = (link) => {
    setEditingFilters(link.slug);
    setEditFilterHidden(new Set(link.hidden_filters || []));
  };

  const toggleFilterHidden = (key) => {
    setEditFilterHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const saveEditFilters = async (slug) => {
    try {
      await axios.patch(
        `${API}/admin/outstanding/share-links/${slug}`,
        { hidden_filters: Array.from(editFilterHidden) },
        { headers: authHeaders },
      );
      setEditingFilters(null);
      await load();
      toast({ title: 'Filter visibility updated' });
    } catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' }); }
  };

  // Feb 2026 — "Add more divisions" flow. `facets.division` on a link
  // may be a legacy scalar string OR an array (multi-select shape).
  // `_currentDivisions` normalises both, `startEditDivisions` opens
  // the inline picker, `saveEditDivisions` merges + PATCHes without
  // regenerating the URL (backend just $set the facets subdoc).
  const _currentDivisions = (link) => {
    const v = link?.facets?.division;
    if (Array.isArray(v)) return v.filter(Boolean);
    if (typeof v === 'string' && v.trim()) return [v.trim()];
    return [];
  };

  const startEditDivisions = (link) => {
    setEditingDivisions(link.slug);
    setEditDivAdd([]);
  };

  const saveEditDivisions = async (link) => {
    const existing = _currentDivisions(link);
    // Union — keep existing order, append only ones the admin picked
    // that aren't already baked. Uses a Set for dedupe.
    const seen = new Set(existing);
    const additions = editDivAdd.filter((d) => d && !seen.has(d));
    if (additions.length === 0) {
      // Nothing new picked — silently close the editor.
      setEditingDivisions(null);
      return;
    }
    const merged = [...existing, ...additions];
    // Preserve scalar shape only when the merged list collapses to one
    // element (i.e. `existing=[]` and one addition). Otherwise send as
    // an array — backend handles both.
    const nextFacetsDivision = merged.length === 1 ? merged[0] : merged;
    try {
      const nextFacets = { ...(link.facets || {}), division: nextFacetsDivision };
      await axios.patch(
        `${API}/admin/outstanding/share-links/${link.slug}`,
        { facets: nextFacets },
        { headers: authHeaders },
      );
      setEditingDivisions(null);
      setEditDivAdd([]);
      await load();
      toast({
        title: `Divisions added · +${additions.length}`,
        description: `Link now covers: ${merged.join(', ')}`,
      });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast({ title: 'Link copied' }); };
  const copyForRecipient = (url) => {
    // "Copy for…" — prompt for a recipient name and append it to the
    // link as a `?n=Foo` query param. The public share page reads this
    // and overrides the greeting name so the SAME base link greets
    // "Mohammed, Good Evening" for one recipient and "Ahmed, Good
    // Evening" for another without minting separate links.
    // Trimmed + limited to 60 chars so a stray paste doesn't produce
    // a giant URL. Blank input falls back to plain copy.
    const raw = window.prompt("Recipient name (personalises the greeting on this link):", "");
    if (raw === null) return;               // user cancelled
    const name = String(raw).trim().slice(0, 60);
    if (!name) { copy(url); return; }
    const sep = url.includes("?") ? "&" : "?";
    const personalised = `${url}${sep}n=${encodeURIComponent(name)}`;
    navigator.clipboard.writeText(personalised);
    toast({
      title: "Personalised link copied",
      description: `Greets "${name}, Good Morning/Afternoon/Evening" on open.`,
    });
  };
  const revoke = async (slug) => { await axios.post(`${API}/admin/outstanding/share-links/${slug}/revoke`, {}, { headers: authHeaders }); await load(); toast({ title: 'Link revoked' }); };
  const restore = async (slug) => { await axios.post(`${API}/admin/outstanding/share-links/${slug}/restore`, {}, { headers: authHeaders }); await load(); toast({ title: 'Link restored' }); };
  const del = async (slug) => {
    if (!window.confirm('Delete this link permanently?')) return;
    await axios.delete(`${API}/admin/outstanding/share-links/${slug}`, { headers: authHeaders });
    await load(); toast({ title: 'Link deleted' });
  };

  const renameLink = async (link) => {
    const next = window.prompt('New label for this share link:', link.label || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed === (link.label || '').trim()) return;
    try {
      await axios.patch(`${API}/admin/outstanding/share-links/${link.slug}`, { label: trimmed }, { headers: authHeaders });
      await load();
      toast({ title: 'Link renamed', description: trimmed || '(cleared)' });
    } catch (e) {
      toast({ title: 'Rename failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  // Feb 2026 — per-link export permission toggles. Sent as an optional
  // bool on the same PATCH endpoint the label/columns/filters edits use.
  // The toggle flips optimistically so the switch feels instant; on API
  // failure we reload the list to snap back to server truth.
  const toggleExportPermission = async (link, field, next) => {
    try {
      await axios.patch(
        `${API}/admin/outstanding/share-links/${link.slug}`,
        { [field]: next },
        { headers: authHeaders },
      );
      await load();
      toast({
        title: next
          ? `${field === 'allow_excel_export' ? 'Excel' : 'PDF'} export allowed`
          : `${field === 'allow_excel_export' ? 'Excel' : 'PDF'} export blocked`,
        description: `Applies to the "${link.label || link.slug}" link.`,
      });
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-links-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">Public share links</h3>
            <p className="text-xs text-neutral-500">Read-only, auto-refreshing outstanding view — perfect for sales-reps.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Global ticker settings — applied to EVERY share link on the
              very next page load. No need to remint links after tweaking. */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="outstanding-ticker-settings">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#0F172A]">Ticker scroll speed</div>
              <div className="text-xs text-neutral-500">Same speed applies to <b>every</b> share link. Lower = faster.</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <input
                type="range"
                min="1"
                max="100"
                step="1"
                value={tickerSpeed}
                onChange={(e) => setTickerSpeed(Number(e.target.value))}
                onMouseDown={() => { isAdjustingSpeedRef.current = true; }}
                onTouchStart={() => { isAdjustingSpeedRef.current = true; }}
                onMouseUp={(e) => { isAdjustingSpeedRef.current = false; saveTickerSpeed(e.target.value); }}
                onTouchEnd={(e) => { isAdjustingSpeedRef.current = false; saveTickerSpeed(e.target.value); }}
                onKeyDown={() => { isAdjustingSpeedRef.current = true; }}
                onKeyUp={(e) => {
                  // Persist on key release (arrow / page-up / etc). Save
                  // fires on release — not on every key repeat — so we
                  // stay compliant with the "one save per adjust"
                  // directive without stranding keyboard-only users.
                  isAdjustingSpeedRef.current = false;
                  saveTickerSpeed(e.target.value);
                }}
                className="w-40 accent-[#0078D4]"
                data-testid="outstanding-ticker-speed-range"
                aria-label="Ticker speed in seconds per full scroll"
              />
              <input
                type="number"
                min="1"
                max="100"
                value={tickerSpeed}
                onChange={(e) => setTickerSpeed(Number(e.target.value))}
                onFocus={() => { isAdjustingSpeedRef.current = true; }}
                onBlur={(e) => { isAdjustingSpeedRef.current = false; saveTickerSpeed(e.target.value); }}
                className="w-20 h-9 rounded-lg border border-neutral-300 px-2 text-sm font-semibold text-[#0F172A] tabular-nums text-right"
                data-testid="outstanding-ticker-speed-input"
              />
              <span className="text-xs text-neutral-600 font-semibold">s / loop</span>
              {savingSpeed && <Loader2 className="w-4 h-4 text-[#0078D4] animate-spin ml-1" />}
            </div>
          </div>

          <div className="rounded-2xl border border-dashed border-[#0078D4]/40 bg-[#DEECF9]/30 p-4">
            <div className="text-sm font-semibold text-[#0F172A] mb-2">Create a new link</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
              <Input placeholder="Label (e.g. Ahmed — sales team)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="outstanding-link-label" />
              <MultiSelectFacet
                testId="outstanding-link-baked-salesman"
                placeholder="Lock salesman"
                options={facets?.salesman || []}
                value={bakedSalesman}
                onChange={setBakedSalesman}
              />
              <MultiSelectFacet
                testId="outstanding-link-baked-division"
                placeholder="Lock division"
                options={facets?.division || []}
                value={bakedDivision}
                onChange={setBakedDivision}
              />
              <MultiSelectFacet
                testId="outstanding-link-baked-dept"
                placeholder="Lock dept: (none)"
                options={facets?.dept || []}
                value={bakedDept}
                onChange={setBakedDept}
              />
            </div>
            <Button onClick={mint} disabled={creating} className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full h-10" data-testid="outstanding-link-create-btn">
              <Link2 className="w-4 h-4 mr-2" /> {creating ? 'Creating…' : 'Create link'}
            </Button>
            <div className="mt-4 pt-4 border-t border-[#0078D4]/20">
              <div className="text-xs font-bold text-neutral-600 uppercase tracking-wide mb-2">Columns audience will see</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-xs">
                <label className="flex items-center gap-1.5 text-neutral-500 italic">
                  <input type="checkbox" checked disabled /> Cust code + Name (always)
                </label>
                {HIDEABLE_COLS.map((c) => (
                  <label key={c.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={colVis[c.key]} onChange={(e) => setColVis((p) => ({ ...p, [c.key]: e.target.checked }))} data-testid={`outstanding-link-col-${c.key}`} />
                    {c.label}
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-neutral-500 mt-2">Uncheck to hide from the audience. Leave all checked to show every column.</div>
            </div>
          </div>
          <div className="space-y-2">
            {loading && <p className="text-sm text-neutral-500 text-center py-4">Loading links…</p>}
            {!loading && links.length === 0 && <p className="text-sm text-neutral-500 text-center py-4">No share links yet.</p>}
            {links.map((l) => (
              <div key={l.slug} className={`rounded-2xl border p-3 ${l.revoked_at ? 'border-red-200 bg-red-50/30' : 'border-neutral-200'}`} data-testid={`outstanding-link-row-${l.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A] truncate flex items-center gap-2">
                      {l.label || <span className="italic text-neutral-500">Untitled</span>}
                      {l.revoked_at && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Revoked</span>}
                    </div>
                    <div className="text-xs text-neutral-500 truncate font-mono">{l.public_url}</div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {l.hits || 0} hits · created {fmtDate(l.created_at)}
                      {l.facets && Object.keys(l.facets).length > 0 && (
                        <span className="ml-2">
                          {Object.entries(l.facets).map(([k, v]) => (
                            <span key={k} className="ml-1 inline-block bg-[#DEECF9] text-[#0078D4] px-1.5 py-0.5 rounded text-[10px] font-semibold">
                              {/* Feb 2026 — v may be a scalar (legacy)
                                  or an array (multi-select). Join arrays
                                  with commas so the badge stays
                                  readable at a glance. */}
                              {k}={Array.isArray(v) ? v.join(', ') : v}
                            </span>
                          ))}
                        </span>
                      )}
                      <span className="ml-2 inline-block bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                        {l.columns ? `${l.columns.length} of ${COLS.length} cols` : 'all columns'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => renameLink(l)} title="Rename link" data-testid={`outstanding-link-edit-name-${l.slug}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => startEditCols(l)} title="Edit columns" data-testid={`outstanding-link-edit-cols-${l.slug}`}><Columns3 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => startEditFilters(l)} title="Edit filters" data-testid={`outstanding-link-edit-filters-${l.slug}`}><EyeOff className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => startEditDivisions(l)} title="Add more divisions to this link" data-testid={`outstanding-link-edit-divisions-${l.slug}`}><Layers className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => copy(l.public_url)} title="Copy link" data-testid={`outstanding-link-copy-${l.slug}`}><Copy className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => copyForRecipient(l.public_url)} title="Copy link personalised for a recipient" data-testid={`outstanding-link-copy-recipient-${l.slug}`}><UserRoundPen className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(l.public_url, '_blank')} data-testid={`outstanding-link-open-${l.slug}`}><Eye className="w-4 h-4" /></Button>
                    {l.revoked_at ? (
                      <Button size="sm" variant="outline" onClick={() => restore(l.slug)} className="text-emerald-700 border-emerald-300" data-testid={`outstanding-link-restore-${l.slug}`}><RotateCcw className="w-4 h-4" /></Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => revoke(l.slug)} className="text-amber-700 border-amber-300" data-testid={`outstanding-link-revoke-${l.slug}`}><Ban className="w-4 h-4" /></Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(l.slug)} className="text-red-600" data-testid={`outstanding-link-delete-${l.slug}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {/* Feb 2026 — per-link export permission toggles. Both
                    default to OFF for existing links minted before this
                    migration. Server side enforces even if a visitor
                    hand-crafts the download URL. */}
                <div className="mt-3 pt-3 border-t border-neutral-200 flex flex-wrap items-center gap-4 text-xs" data-testid={`outstanding-link-export-perms-${l.slug}`}>
                  <span className="text-neutral-500 uppercase tracking-wide font-semibold">Export permissions</span>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch
                      checked={!!l.allow_excel_export}
                      onCheckedChange={(v) => toggleExportPermission(l, 'allow_excel_export', !!v)}
                      data-testid={`outstanding-link-allow-excel-${l.slug}`}
                    />
                    <span className="text-neutral-700">Allow Excel export</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch
                      checked={!!l.allow_pdf_export}
                      onCheckedChange={(v) => toggleExportPermission(l, 'allow_pdf_export', !!v)}
                      data-testid={`outstanding-link-allow-pdf-${l.slug}`}
                    />
                    <span className="text-neutral-700">Allow PDF export</span>
                  </label>
                </div>
                {editingCols === l.slug && (
                  <div className="mt-3 pt-3 border-t border-neutral-200 bg-slate-50 -mx-3 -mb-3 p-3 rounded-b-2xl" data-testid={`outstanding-link-cols-editor-${l.slug}`}>
                    <div className="text-xs font-bold text-neutral-600 uppercase tracking-wide mb-2">Columns visible to this audience</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-xs mb-3">
                      <label className="flex items-center gap-1.5 text-neutral-500 italic">
                        <input type="checkbox" checked disabled /> Cust code + Name (always)
                      </label>
                      {HIDEABLE_COLS.map((c) => (
                        <label key={c.key} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editColVis[c.key] ?? true}
                            onChange={(e) => setEditColVis((p) => ({ ...p, [c.key]: e.target.checked }))}
                            data-testid={`outstanding-link-edit-col-${l.slug}-${c.key}`}
                          />
                          {c.label}
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditingCols(null)} className="rounded-full">Cancel</Button>
                      <Button size="sm" onClick={() => saveEditCols(l.slug)} className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full" data-testid={`outstanding-link-cols-save-${l.slug}`}>Save columns</Button>
                    </div>
                  </div>
                )}
                {editingFilters === l.slug && (
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <div className="text-xs font-bold text-neutral-600 uppercase tracking-wide mb-2">Filter widgets visible to this audience</div>
                    <div className="text-[11px] text-neutral-500 mb-2">Tick a filter to <b>hide</b> it from the public view (desktop &amp; mobile).</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-xs mb-3">
                      {FILTER_KEYS.map((f) => (
                        <label key={f.key} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editFilterHidden.has(f.key)}
                            onChange={() => toggleFilterHidden(f.key)}
                            data-testid={`outstanding-link-edit-filter-${l.slug}-${f.key}`}
                          />
                          <span className={editFilterHidden.has(f.key) ? 'line-through text-red-500' : ''}>{f.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditingFilters(null)} className="rounded-full">Cancel</Button>
                      <Button size="sm" onClick={() => saveEditFilters(l.slug)} className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full" data-testid={`outstanding-link-filters-save-${l.slug}`}>Save filters</Button>
                    </div>
                  </div>
                )}
                {editingDivisions === l.slug && (() => {
                  const cur = _currentDivisions(l);
                  const curSet = new Set(cur);
                  const availableOptions = (facets?.division || []).filter((opt) => !curSet.has(opt));
                  return (
                    <div
                      className="mt-3 pt-3 border-t border-neutral-200 bg-indigo-50/40 -mx-3 -mb-3 p-3 rounded-b-2xl"
                      data-testid={`outstanding-link-divisions-editor-${l.slug}`}
                    >
                      <div className="text-xs font-bold text-neutral-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-indigo-600" />
                        Add more divisions to this link
                      </div>
                      <div className="text-[11px] text-neutral-500 mb-2">
                        The link URL stays the same — new divisions are appended to the ones already attached.
                      </div>
                      <div className="mb-3">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">
                          Currently attached ({cur.length})
                        </div>
                        {cur.length === 0 ? (
                          <div
                            className="text-[11px] italic text-neutral-400"
                            data-testid={`outstanding-link-divisions-current-empty-${l.slug}`}
                          >
                            No division locked yet — the link currently shows every division.
                          </div>
                        ) : (
                          <div
                            className="flex flex-wrap gap-1.5"
                            data-testid={`outstanding-link-divisions-current-${l.slug}`}
                          >
                            {cur.map((d) => (
                              <span
                                key={d}
                                className="inline-flex items-center gap-1 rounded-full bg-indigo-100 border border-indigo-300 text-indigo-800 px-2 py-0.5 text-[11px] font-semibold"
                                data-testid={`outstanding-link-divisions-current-chip-${l.slug}-${d}`}
                              >
                                {d}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">
                          Add divisions
                        </div>
                        {availableOptions.length === 0 ? (
                          <div
                            className="text-[11px] italic text-neutral-400"
                            data-testid={`outstanding-link-divisions-add-empty-${l.slug}`}
                          >
                            Every available division is already attached to this link.
                          </div>
                        ) : (
                          <MultiSelectFacet
                            testId={`outstanding-link-divisions-add-${l.slug}`}
                            placeholder="Pick divisions to add"
                            options={availableOptions}
                            value={editDivAdd}
                            onChange={setEditDivAdd}
                          />
                        )}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingDivisions(null); setEditDivAdd([]); }}
                          className="rounded-full"
                          data-testid={`outstanding-link-divisions-cancel-${l.slug}`}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveEditDivisions(l)}
                          disabled={editDivAdd.length === 0}
                          className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full disabled:opacity-50"
                          data-testid={`outstanding-link-divisions-save-${l.slug}`}
                        >
                          Add {editDivAdd.length > 0 ? `${editDivAdd.length} division${editDivAdd.length === 1 ? '' : 's'}` : 'divisions'}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailReportModal = ({ onClose, authHeaders, snapshot, filters, columns }) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('Customer Outstanding Report');
  const [note, setNote] = useState('');
  const [sendPdf, setSendPdf] = useState(true);
  const [sendXlsx, setSendXlsx] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  const send = async () => {
    const recipients = to.split(/[,\s]+/).map((s) => s.trim()).filter((s) => s.includes('@'));
    if (recipients.length === 0) {
      toast({ title: 'Add at least one recipient email' });
      return;
    }
    const formats = [];
    if (sendPdf) formats.push('pdf');
    if (sendXlsx) formats.push('xlsx');
    if (formats.length === 0) {
      toast({ title: 'Pick at least one attachment format' });
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/outstanding/send-email`, {
        to: recipients, subject, note, formats,
        snapshot_id: snapshot?.id, columns, ...filters,
      }, { headers: authHeaders });
      setResult(r.data);
      toast({ title: `Sent to ${r.data.delivered.length} of ${recipients.length}` });
    } catch (e) {
      toast({ title: 'Send failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-email-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#DEECF9] flex items-center justify-center"><Mail className="w-5 h-5 text-[#0078D4]" /></div>
            <div>
              <h3 className="text-lg font-bold text-[#0F172A]">Email report</h3>
              <p className="text-xs text-neutral-500">Send the outstanding report to one or more recipients (max 20).</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">To (comma-separated)</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="manager@mrpco.net, sales-head@mrpco.net" data-testid="outstanding-email-to" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outstanding-email-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Note (optional — appears above the summary)</label>
            <textarea className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm h-20 resize-none" value={note} onChange={(e) => setNote(e.target.value)} data-testid="outstanding-email-note" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sendPdf} onChange={(e) => setSendPdf(e.target.checked)} data-testid="outstanding-email-pdf" /> Attach PDF</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sendXlsx} onChange={(e) => setSendXlsx(e.target.checked)} data-testid="outstanding-email-xlsx" /> Attach Excel</label>
          </div>
          {result && (
            <div className={`rounded-2xl p-4 text-sm ${result.errors?.length ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
              <div className="font-semibold mb-1">{result.delivered?.length || 0} delivered · {result.errors?.length || 0} failed</div>
              {result.errors?.length > 0 && <ul className="text-xs mt-1 text-red-700">{result.errors.map((e, i) => <li key={i}>{e.to}: {e.reason}</li>)}</ul>}
            </div>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
            <Button onClick={send} disabled={sending} className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white" data-testid="outstanding-email-send-btn">
              <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscribersModal = ({ onClose, authHeaders, facets }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [tiers, setTiers] = useState({ '60d': true, '90d': true, '180d': true, 'over180d': true });
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const TIERS = [
    { key: '60d',       label: '60 days' },
    { key: '90d',       label: '90 days' },
    { key: '180d',      label: '180 days' },
    { key: 'over180d',  label: '>180 days' },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/outstanding/notification-subs`, { headers: authHeaders });
      setSubs(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!email.includes('@')) { toast({ title: 'Enter a valid email' }); return; }
    setCreating(true);
    try {
      const thresholds = TIERS.filter((t) => tiers[t.key]).map((t) => t.key);
      await axios.post(`${API}/admin/outstanding/notification-subs`,
        { email, name, category, thresholds }, { headers: authHeaders });
      setEmail(''); setName(''); setCategory('');
      await load();
      toast({ title: 'Subscriber added' });
    } catch (e) {
      toast({ title: 'Create failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/outstanding/notification-subs/${id}`, { headers: authHeaders });
      await load();
      toast({ title: 'Subscriber deleted' });
    } catch (e) { toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' }); }
  };

  const toggle = async (sub) => {
    try {
      await axios.patch(`${API}/admin/outstanding/notification-subs/${sub.id}`,
        { ...sub, disabled: !sub.disabled }, { headers: authHeaders });
      await load();
    } catch (e) { toast({ title: 'Update failed', description: e.response?.data?.detail || 'Try again.' }); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-subs-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">Notification subscribers</h3>
            <p className="text-xs text-neutral-500">Get an email whenever a customer crosses 60 / 90 / 180 / &gt;180 day thresholds — filtered by category.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="rounded-2xl border border-dashed border-[#0078D4]/40 bg-[#DEECF9]/30 p-4">
            <div className="text-sm font-semibold text-[#0F172A] mb-2">Add subscriber</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <Input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="outstanding-sub-email" />
              <Input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="outstanding-sub-name" />
              <select className="h-10 rounded-lg border border-neutral-200 px-3 text-sm bg-white" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="outstanding-sub-category">
                <option value="">Category: (all)</option>
                {(facets?.category || []).map((c) => <option key={c} value={c}>Category: {c}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-3 mb-3 text-sm">
              {TIERS.map((t) => (
                <label key={t.key} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={tiers[t.key]} onChange={(e) => setTiers((p) => ({ ...p, [t.key]: e.target.checked }))} data-testid={`outstanding-sub-tier-${t.key}`} />
                  {t.label}
                </label>
              ))}
            </div>
            <Button onClick={create} disabled={creating} className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full h-10" data-testid="outstanding-sub-create-btn">
              <Plus className="w-4 h-4 mr-2" /> {creating ? 'Adding…' : 'Add subscriber'}
            </Button>
          </div>
          <div className="space-y-2">
            {loading && <p className="text-sm text-neutral-500 text-center py-4">Loading…</p>}
            {!loading && subs.length === 0 && <p className="text-sm text-neutral-500 text-center py-4">No subscribers yet.</p>}
            {subs.map((s) => (
              <div key={s.id} className={`rounded-2xl border p-3 flex items-center gap-3 ${s.disabled ? 'border-neutral-200 bg-neutral-50/50 opacity-60' : 'border-neutral-200'}`} data-testid={`outstanding-sub-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0F172A] truncate">
                    {s.name || <span className="italic text-neutral-500">Unnamed</span>} · <span className="font-mono">{s.email}</span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    <span className="inline-block bg-[#DEECF9] text-[#0078D4] px-2 py-0.5 rounded text-[10px] font-semibold mr-1">
                      {s.category || 'all categories'}
                    </span>
                    Tiers: {(s.thresholds || []).join(', ')}
                    {s.disabled && <span className="ml-2 text-red-600 font-semibold">DISABLED</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`outstanding-sub-toggle-${s.id}`}>{s.disabled ? 'Enable' : 'Disable'}</Button>
                <Button size="sm" variant="ghost" onClick={() => del(s.id)} className="text-red-600" data-testid={`outstanding-sub-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOutstanding;